import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Popover,
  Collapse,
  Tooltip,
  Divider,
} from '@mui/material';
import { FiTrash2, FiX } from 'react-icons/fi';
import {
  ArrowBack,
  Campaign as CampaignIcon,
  Refresh,
  Payments,
  Comment as CommentIcon,
  Send,
  Notifications,
  Person,
  Search,
  Save,
  Check,
  LocalOffer,
  Close,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  InfoOutlined,
  CheckCircle,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { FaRupeeSign } from 'react-icons/fa';
import { contactsAPI } from '../services/api';
import { useSelector } from 'react-redux';
import { selectGlobalTags } from '../store/slices/tagsSlice';
import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import { showSuccess, showError } from '../utils/toast';
import { getSortIcon } from '../utils/sortIcon';
import { colors } from '../theme/modernTheme';
import { useDebounce } from '../hooks/useDebounce';
import TagAutocomplete from '../components/TagAutocomplete';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { MODULE_PERMISSIONS } from '../utils/modulePermissions';
import { usePermissionNavigation } from '../hooks/usePermissionNavigation';
import { useContactDetailCache } from '../hooks/useContactDetailCache';
import { FiHeart } from 'react-icons/fi';
import { getFrequencyLabel } from '../utils/frequencyLabel';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import { paymentMethodLabel } from '../utils/paymentMethodLabel';
import { GrDocumentDownload } from 'react-icons/gr';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 1, borderShadow: 'none' }}>{children}</Box>}
    </div>
  );
}

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const navigateWithPermission = usePermissionNavigation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromDonation = searchParams.get('from') === 'donation';
  const fromTransaction = searchParams.get('from') === 'transaction';
  const { user: currentUser, hasModulePermission } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const canAddDonation = hasModulePermission(MODULE_PERMISSIONS.DONATION);
  const tagPermission = hasModulePermission(MODULE_PERMISSIONS.TAGS);

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Receipt preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [previewTransaction, setPreviewTransaction] = useState(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    type: 'INDIVIDUAL',
    first_name: '',
    last_name: '',
    organization_name: '',
    primary_email: '',
    secondary_email: '',
    phone_1: '',
    phone_2: '',
    phone_1_country_code: '91',
    phone_2_country_code: '91',
    pan_card_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    preferred_channel: 'EMAIL',
    do_not_email: false,
    do_not_call: false,
    do_not_whatsapp: false,
    notes: '',
  });

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});

  // Tags state
  const availableTags = useSelector(selectGlobalTags);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [tempSelectedTags, setTempSelectedTags] = useState([]);
  const [tagAnchorEl, setTagAnchorEl] = useState(null);

  // Use Redux cache for all tab data
  const {
    emailCampaigns,
    whatsappCampaigns,
    donations,
    reminders,
    transactions,
    comments,
    loadEmailCampaigns,
    loadWhatsappCampaigns,
    loadDonations,
    loadReminders,
    loadTransactions,
    loadComments,
    addCommentToCache,
    deleteCommentFromCache,
    clearCache,
  } = useContactDetailCache(id);

  // Search and filter state (not cached)
  const [emailCampaignSearch, setEmailCampaignSearch] = useState('');
  const debouncedEmailCampaignSearch = useDebounce(emailCampaignSearch, 500);
  const [emailCampaignSortBy, setEmailCampaignSortBy] = useState('last_activity_at');
  const [emailCampaignSortOrder, setEmailCampaignSortOrder] = useState('desc');

  const [whatsappCampaignSearch, setWhatsappCampaignSearch] = useState('');
  const debouncedWhatsappCampaignSearch = useDebounce(whatsappCampaignSearch, 500);
  const [whatsappCampaignSortBy, setWhatsappCampaignSortBy] = useState('last_activity_at');
  const [whatsappCampaignSortOrder, setWhatsappCampaignSortOrder] = useState('desc');

  const [donationSortBy, setDonationSortBy] = useState('pledge_start_date');
  const [donationSortOrder, setDonationSortOrder] = useState('desc');
  const [donationTypeFilter, setDonationTypeFilter] = useState('');
  const [appliedDonationTypeFilter, setAppliedDonationTypeFilter] = useState('');
  const [donationStatusFilter, setDonationStatusFilter] = useState('');
  const [appliedDonationStatusFilter, setAppliedDonationStatusFilter] = useState('');

  const [commentSearch, setCommentSearch] = useState('');
  const [appliedCommentSearch, setAppliedCommentSearch] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentStartDate, setCommentStartDate] = useState('');
  const [appliedCommentStartDate, setAppliedCommentStartDate] = useState('');
  const [commentEndDate, setCommentEndDate] = useState('');
  const [appliedCommentEndDate, setAppliedCommentEndDate] = useState('');
  const [commentUserFilter, setCommentUserFilter] = useState('');
  const [appliedCommentUserFilter, setAppliedCommentUserFilter] = useState('');

  const [reminderSortBy, setReminderSortBy] = useState('next_pledge_date');
  const [reminderSortOrder, setReminderSortOrder] = useState('desc');
  const [reminderTypeFilter, setReminderTypeFilter] = useState('');
  const [appliedReminderTypeFilter, setAppliedReminderTypeFilter] = useState('');
  const [reminderStatusFilter, setReminderStatusFilter] = useState('');
  const [appliedReminderStatusFilter, setAppliedReminderStatusFilter] = useState('');

  const [transactionSortBy, setTransactionSortBy] = useState('payment_date');
  const [transactionSortOrder, setTransactionSortOrder] = useState('desc');
  const [transactionPaymentMethodFilter, setTransactionPaymentMethodFilter] = useState('');
  const [appliedTransactionPaymentMethodFilter, setAppliedTransactionPaymentMethodFilter] =
    useState('');
  const [transactionStartDate, setTransactionStartDate] = useState('');
  const [appliedTransactionStartDate, setAppliedTransactionStartDate] = useState('');
  const [transactionEndDate, setTransactionEndDate] = useState('');
  const [appliedTransactionEndDate, setAppliedTransactionEndDate] = useState('');

  const canEdit = currentUser?.role === 'ADMIN' || currentUser?.role === 'STAFF';

  useEffect(() => {
    loadContact();

    // Check if we should open in edit mode (from navigation state or query parameter)
    const searchParams = new URLSearchParams(location.search);
    if ((location.state?.editMode || searchParams.get('edit') === 'true') && id !== 'new') {
      setIsEditMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Scroll event listener for header background
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ref for comments container
  const commentsEndRef = React.useRef(null);

  useEffect(() => {
    // Load data when tab changes (uses cache automatically)
    if (activeTab === 0) {
      // Load comments for Contact Details tab (shown in Column 2)
      loadComments();
    } else if (activeTab === 1) {
      loadEmailCampaigns();
    } else if (activeTab === 2) {
      loadWhatsappCampaigns();
    } else if (activeTab === 3) {
      loadDonations();
    } else if (activeTab === 4) {
      loadReminders();
    } else if (activeTab === 5) {
      loadTransactions();
    } else if (activeTab === 6) {
      loadComments();
    }
  }, [
    activeTab,
    loadEmailCampaigns,
    loadWhatsappCampaigns,
    loadDonations,
    loadReminders,
    loadTransactions,
    loadComments,
  ]);

  // Reload email campaigns when debounced search changes
  useEffect(() => {
    if (activeTab === 1 && emailCampaigns.data.length > 0) {
      loadEmailCampaigns(true); // Force reload for search
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmailCampaignSearch]);

  // Reload WhatsApp campaigns when debounced search changes
  useEffect(() => {
    if (activeTab === 2 && whatsappCampaigns.data.length > 0) {
      loadWhatsappCampaigns(true); // Force reload for search
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedWhatsappCampaignSearch]);

  const loadContact = async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    // Skip loading if creating a new contact
    if (id === 'new') {
      setLoading(false);
      setIsEditMode(true);
      return;
    }

    setLoading(true);
    try {
      const response = await contactsAPI.getOne(id);
      const contactData = response.data;
      setContact(contactData);

      // Initialize form data with contact data, ensuring strings are never null
      setFormData({
        type: contactData.type || 'INDIVIDUAL',
        first_name: contactData.first_name || '',
        last_name: contactData.last_name || '',
        organization_name: contactData.organization_name || '',
        primary_email: contactData.primary_email || '',
        secondary_email: contactData.secondary_email || '',
        phone_1: contactData.phone_1 || '',
        phone_1_country_code: contactData.phone_1_country_code || '91',
        phone_2: contactData.phone_2 || '',
        phone_2_country_code: contactData.phone_2_country_code || '91',
        pan_card_number: contactData.pan_card_number || '',
        address_line1: contactData.address_line1 || '',
        address_line2: contactData.address_line2 || '',
        city: contactData.city || '',
        state: contactData.state || '',
        postal_code: contactData.postal_code || '',
        country: contactData.country || 'India',
        preferred_channel: contactData.preferred_channel || 'EMAIL',
        do_not_email: Boolean(contactData.do_not_email),
        do_not_call: Boolean(contactData.do_not_call),
        do_not_whatsapp: Boolean(contactData.do_not_whatsapp),
        notes: contactData.notes || '',
      });
      // Initialize selected tags
      setSelectedTags(contactData.tags || []);

      // Load transactions for the Recent Top 10 Transactions panel
      try {
        await loadTransactions();
      } catch (transactionError) {
        // Don't fail contact loading if transactions fail to load
        console.warn('Failed to load transactions for Recent Top 10 panel:', transactionError);
      }
    } catch (error) {
      showError('Failed to load contact details');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (id === 'new') {
      // If coming from donation page, go back to donation page
      if (fromDonation) {
        navigate('/donations/new');
      } else if (fromTransaction) {
        navigate('/transactions/new');
      } else {
        navigate('/contacts');
      }
    } else {
      setIsEditMode(false);
      setFormData({
        ...contact,
        do_not_email: Boolean(contact.do_not_email),
        do_not_call: Boolean(contact.do_not_call),
        do_not_whatsapp: Boolean(contact.do_not_whatsapp),
      });
    }
  };

  // Validation functions
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'first_name':
        if (!value || value.trim() === '') {
          error = 'First name is required';
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          error = 'First name can only contain letters, spaces, hyphens, and apostrophes';
        } else if (value.length < 2) {
          error = 'First name must be at least 2 characters long';
        } else if (value.length > 255) {
          error = 'First name cannot exceed 255 characters';
        }
        break;

      case 'last_name':
        if (value && !/^[a-zA-Z\s'-]+$/.test(value)) {
          error = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
        } else if (value && value.length < 2) {
          error = 'Last name must be at least 2 characters long';
        } else if (value && value.length > 255) {
          error = 'Last name cannot exceed 255 characters';
        }
        break;

      case 'organization_name':
        if (formData.type === 'ORGANIZATION' && (!value || value.trim() === '')) {
          error = 'Organization name is required';
        } else if (value && value.length < 2) {
          error = 'Organization name must be at least 2 characters long';
        } else if (value && value.length > 255) {
          error = 'Organization name cannot exceed 255 characters';
        }
        break;

      case 'primary_email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        } else if (value && value.length > 255) {
          error = 'Email address cannot exceed 255 characters';
        }
        break;

      case 'secondary_email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        } else if (value && value.length > 255) {
          error = 'Email address cannot exceed 255 characters';
        }
        break;

      case 'phone_1_country_code':
        if (value && !/^[1-9]\d{0,3}$/.test(value)) {
          error = 'Country code must be 1-4 digits (e.g., 1, 91, 971) without + prefix';
        }
        break;

      case 'phone_2_country_code':
        if (value && !/^[1-9]\d{0,3}$/.test(value)) {
          error = 'Country code must be 1-4 digits (e.g., 1, 91, 971) without + prefix';
        }
        break;

      case 'phone_1':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'Phone must be exactly 10 digits';
        }
        break;

      case 'phone_2':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'Phone must be exactly 10 digits';
        }
        break;

      case 'pan_card_number':
        if (value && value.trim() !== '') {
          // PAN format: AAAAA9999A (5 letters, 4 digits, 1 letter)
          const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
          if (!panRegex.test(value.toUpperCase())) {
            error =
              'PAN must be 10 characters and formate of 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)';
          }
        }
        break;

      case 'postal_code':
        if (value && !/^\d+$/.test(value)) {
          error = 'Postal code should contain only numbers';
        } else if (value && value.length > 10) {
          error = 'Postal code should not exceed 10 digits';
        }
        break;

      case 'address_line1':
        if (value && value.length > 255) {
          error = 'Address line 1 cannot exceed 255 characters';
        }
        break;

      case 'address_line2':
        if (value && value.length > 255) {
          error = 'Address line 2 cannot exceed 255 characters';
        }
        break;

      case 'city':
        if (value && value.length > 100) {
          error = 'City cannot exceed 100 characters';
        }
        break;

      case 'state':
        if (value && value.length > 100) {
          error = 'State cannot exceed 100 characters';
        }
        break;

      case 'country':
        if (value && value.length > 100) {
          error = 'Country cannot exceed 100 characters';
        }
        break;

      case 'notes':
        if (value && value.length > 1000) {
          error = 'Notes cannot exceed 1000 characters';
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const errors = {};

    // Validate based on contact type
    if (formData.type === 'INDIVIDUAL') {
      errors.first_name = validateField('first_name', formData.first_name);
      errors.last_name = validateField('last_name', formData.last_name);
    } else {
      errors.organization_name = validateField('organization_name', formData.organization_name);
    }

    // Check if either email or phone is provided
    const hasEmail = formData.primary_email && formData.primary_email.trim() !== '';
    const hasPhone = formData.phone_1 && formData.phone_1.trim() !== '';

    if (!hasEmail && !hasPhone) {
      // Show error on both primary_email and phone_1 fields
      errors.primary_email = 'Either Primary Email or Primary Phone is required';
      errors.phone_1 = 'Either Primary Email or Primary Phone is required';
    }

    // Validate all fields
    if (!errors.primary_email) {
      errors.primary_email = validateField('primary_email', formData.primary_email);
    }
    errors.secondary_email = validateField('secondary_email', formData.secondary_email);
    errors.phone_1_country_code = validateField(
      'phone_1_country_code',
      formData.phone_1_country_code,
    );
    if (!errors.phone_1) {
      errors.phone_1 = validateField('phone_1', formData.phone_1);
    }

    // Country code is mandatory when phone number is provided
    if (formData.phone_1 && formData.phone_1.trim() !== '') {
      if (!formData.phone_1_country_code || formData.phone_1_country_code.trim() === '') {
        errors.phone_1_country_code = 'Country code is required when phone number is provided';
      }
    }

    errors.phone_2_country_code = validateField(
      'phone_2_country_code',
      formData.phone_2_country_code,
    );
    errors.phone_2 = validateField('phone_2', formData.phone_2);

    // Country code is mandatory when phone number is provided
    if (formData.phone_2 && formData.phone_2.trim() !== '') {
      if (!formData.phone_2_country_code || formData.phone_2_country_code.trim() === '') {
        errors.phone_2_country_code = 'Country code is required when phone number is provided';
      }
    }

    errors.address_line1 = validateField('address_line1', formData.address_line1);
    errors.address_line2 = validateField('address_line2', formData.address_line2);
    errors.city = validateField('city', formData.city);
    errors.state = validateField('state', formData.state);
    errors.postal_code = validateField('postal_code', formData.postal_code);
    errors.country = validateField('country', formData.country);
    errors.notes = validateField('notes', formData.notes);

    // Remove empty errors
    Object.keys(errors).forEach((key) => {
      if (!errors[key]) delete errors[key];
    });

    return errors;
  };

  // isFormValid removed - save button is always enabled
  // const isFormValid = React.useMemo(() => {
  //   ... validation logic removed
  // }, [formData, validationErrors]);

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // For phone fields, only allow positive numbers (no special characters or negative)
    if ((name === 'phone_1' || name === 'phone_2') && type !== 'checkbox') {
      // Remove any non-digit characters and ensure no negative sign
      newValue = value.replace(/[^0-9]/g, '');
      // Limit to 10 digits
      if (newValue.length > 10) {
        newValue = newValue.slice(0, 10);
      }
    }

    // For country code fields, only allow digits and limit to 4
    if (
      (name === 'phone_1_country_code' || name === 'phone_2_country_code') &&
      type !== 'checkbox'
    ) {
      newValue = value.replace(/[^0-9]/g, '');
      // Limit to 4 digits
      if (newValue.length > 4) {
        newValue = newValue.slice(0, 4);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate field on change
    if (type !== 'checkbox') {
      const error = validateField(name, newValue);
      setValidationErrors((prev) => {
        const newErrors = {
          ...prev,
          [name]: error,
        };

        // Clear "Either Primary Email or Primary Phone is required" error from both fields
        // when user starts filling either primary_email or phone_1
        if ((name === 'primary_email' || name === 'phone_1') && newValue.trim()) {
          // Check if the error is the "either/or" error
          if (newErrors.primary_email === 'Either Primary Email or Primary Phone is required') {
            delete newErrors.primary_email;
          }
          if (newErrors.phone_1 === 'Either Primary Email or Primary Phone is required') {
            delete newErrors.phone_1;
          }
        }

        return newErrors;
      });
    }
  };

  // Common function to handle tag assignment
  const assignTagsToContact = async (contactId) => {
    if (selectedTags.length > 0) {
      for (const tag of selectedTags) {
        try {
          await contactsAPI.assignTag(contactId, tag.id);
        } catch (tagError) {
          // Silently ignore tag assignment errors
        }
      }
    }
  };

  // Common error handler for save operations
  const handleSaveError = (err) => {
    if (err.error?.code === 'VALIDATION_ERROR') {
      const errors = err.error.details?.field_errors || {};
      setValidationErrors(errors);
      // Don't show toast - field errors are displayed below each field
    } else if (err.error?.code === 'DUPLICATE_CONTACT') {
      const errorMsg =
        err.error?.message || 'A contact with this email or phone number already exists';
      // For duplicate errors, show which field has the duplicate
      if (err.error?.details?.field) {
        setValidationErrors({
          [err.error.details.field]: errorMsg,
        });
      } else {
        showError(errorMsg);
      }
    } else {
      const errorMsg = err.message || err.error?.message || 'Failed to save contact';
      showError(errorMsg);
    }
  };

  const handleSave = async () => {
    // Validate form before saving
    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Don't show toast - field errors are displayed below each field
      // The _form error will be displayed in the UI
      return;
    }

    setIsSaving(true);

    try {
      let contactId = id;

      // Prepare data for backend
      const submitData = {
        ...formData,
        phone_1_country_code: formData.phone_1 ? formData.phone_1_country_code : null,
        phone_1: formData.phone_1 || null,
        phone_2_country_code: formData.phone_2 ? formData.phone_2_country_code : null,
        phone_2: formData.phone_2 || null,
        country: formData.country || null, // Send null if empty
        state: formData.state || null, // Send null if empty
        city: formData.city || null, // Send null if empty
        address: formData.address || null, // Send null if empty
      };

      if (id === 'new') {
        // Create new contact
        const response = await contactsAPI.create(submitData);
        contactId = response.data.id;

        // Assign tags to the new contact
        await assignTagsToContact(contactId);

        showSuccess('Contact created successfully');

        // If coming from donation page, redirect back with new contact ID
        if (fromDonation && contactId) {
          navigateWithPermission(`/donations/new?newContactId=${contactId}`);
        } else if (fromTransaction && contactId) {
          navigateWithPermission(`/transactions/new?newContactId=${contactId}`);
        } else {
          navigate(`/contacts`);
        }
      } else {
        // Update existing contact
        await contactsAPI.update(id, submitData);

        // Sync tags - compare current tags with selected tags
        const currentTagIds = (contact.tags || []).map((t) => t.id);
        const selectedTagIds = selectedTags.map((t) => t.id);

        // Remove tags that are no longer selected
        const tagsToRemove = currentTagIds.filter((tagId) => !selectedTagIds.includes(tagId));
        for (const tagId of tagsToRemove) {
          try {
            await contactsAPI.removeTag(id, tagId);
          } catch (tagError) {}
        }

        // Add new tags
        const tagsToAdd = selectedTagIds.filter((tagId) => !currentTagIds.includes(tagId));
        for (const tagId of tagsToAdd) {
          try {
            await contactsAPI.assignTag(id, tagId);
          } catch (tagError) {}
        }

        showSuccess('Contact updated successfully');
        setIsEditMode(false);
        clearCache(); // Clear cache to force reload on next tab visit
        await loadContact();
      }
    } catch (err) {
      handleSaveError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndAddDonation = async () => {
    // Use the same validation as handleSave
    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Don't show toast - field errors are displayed below each field
      // The _form error will be displayed in the UI
      return;
    }

    setIsSaving(true);

    try {
      // Prepare data with separate phone fields for backend
      const submitData = {
        ...formData,
        phone_1_country_code: formData.phone_1 ? formData.phone_1_country_code : null,
        phone_1: formData.phone_1 || null,
        phone_2_country_code: formData.phone_2 ? formData.phone_2_country_code : null,
        phone_2: formData.phone_2 || null,
      };

      // Create new contact
      const response = await contactsAPI.create(submitData);
      const contactId = response.data.id;

      // Assign tags to the new contact
      await assignTagsToContact(contactId);

      showSuccess('Contact created successfully');

      // Navigate to donation form with contact ID
      navigateWithPermission('/donations/new', { state: { contactId } });
    } catch (err) {
      handleSaveError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await contactsAPI.delete(id);
      showSuccess('Contact deleted successfully');
      navigate('/contacts');
    } catch (error) {
      showError('Failed to delete contact');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      showError('Please enter a comment');
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await contactsAPI.createContactComment(id, {
        comment_text: newComment,
      });
      addCommentToCache(response.data); // Update cache immediately
      setNewComment('');
      showSuccess('Comment added successfully');
    } catch (error) {
      showError('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await contactsAPI.deleteContactComment(id, commentId);
      deleteCommentFromCache(commentId); // Update cache immediately
      showSuccess('Comment deleted successfully');
    } catch (error) {
      const errorMessage = error?.error?.message || 'Failed to delete comment';
      showError(errorMessage);
    }
  };

  // Transaction receipt handlers
  const handleDownloadTransactionReceipt = async (transaction) => {
    const { downloadTransactionReceipt } = await import('../utils/receiptHelpers');
    await downloadTransactionReceipt(transaction, {
      showLoading,
      hideLoading,
      showSuccess,
      showError,
    });
  };

  const handleSendTransactionReceiptEmail = async (transaction) => {
    const { sendTransactionReceiptEmail } = await import('../utils/receiptHelpers');
    await sendTransactionReceiptEmail(transaction, {
      showLoading,
      hideLoading,
      showSuccess,
      showError,
    });
  };

  const handleSendTransactionReceiptWhatsApp = async (transaction) => {
    const { sendTransactionReceiptWhatsApp } = await import('../utils/receiptHelpers');
    await sendTransactionReceiptWhatsApp(transaction, {
      showLoading,
      hideLoading,
      showSuccess,
      showError,
    });
  };

  // Open receipt preview modal
  const handlePreviewReceipt = async (transaction) => {
    try {
      showLoading();
      const { fetchReceiptPreview } = await import('../utils/receiptHelpers');
      const data = await fetchReceiptPreview(transaction.id);
      setPreviewHTML(data.html);
      setPreviewTransaction({
        ...transaction,
        ...data.transaction,
      });
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
      showError('Failed to load receipt preview');
    } finally {
      hideLoading();
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewHTML('');
    setPreviewTransaction(null);
  };

  // Tag editing handlers
  const handleTagsClick = (event) => {
    if (!tagPermission) return;
    setTagAnchorEl(event.currentTarget);
    setIsEditingTags(true);
    setTempSelectedTags(selectedTags);
  };

  const handleTagsClose = () => {
    setTagAnchorEl(null);
    setIsEditingTags(false);
    setTempSelectedTags([]);
  };

  const handleTagsSave = async () => {
    try {
      // Sync tags - compare current tags with temp selected tags
      const currentTagIds = (contact.tags || []).map((t) => t.id);
      const tempSelectedTagIds = tempSelectedTags.map((t) => t.id);

      // Remove tags that are no longer selected
      const tagsToRemove = currentTagIds.filter((tagId) => !tempSelectedTagIds.includes(tagId));
      for (const tagId of tagsToRemove) {
        try {
          await contactsAPI.removeTag(id, tagId);
        } catch (tagError) {}
      }

      // Add new tags
      const tagsToAdd = tempSelectedTagIds.filter((tagId) => !currentTagIds.includes(tagId));
      for (const tagId of tagsToAdd) {
        try {
          await contactsAPI.assignTag(id, tagId);
        } catch (tagError) {}
      }

      setSelectedTags(tempSelectedTags);
      await loadContact();
      showSuccess('Tags updated successfully');
      handleTagsClose();
    } catch (error) {
      showError('Failed to update tags');
    }
  };

  // Check if filters are active for each tab
  const hasEmailCampaignFilters = () => {
    return emailCampaignSearch;
  };

  const hasWhatsappCampaignFilters = () => {
    return whatsappCampaignSearch;
  };

  const hasDonationFilters = () => {
    return (
      donationTypeFilter !== appliedDonationTypeFilter ||
      donationStatusFilter !== appliedDonationStatusFilter
    );
  };

  const hasReminderFilters = () => {
    return (
      reminderTypeFilter !== appliedReminderTypeFilter ||
      reminderStatusFilter !== appliedReminderStatusFilter
    );
  };

  const hasTransactionFilters = () => {
    return (
      transactionPaymentMethodFilter !== appliedTransactionPaymentMethodFilter ||
      transactionStartDate !== appliedTransactionStartDate ||
      transactionEndDate !== appliedTransactionEndDate
    );
  };

  const hasCommentFilters = () => {
    return (
      commentSearch !== appliedCommentSearch ||
      commentStartDate !== appliedCommentStartDate ||
      commentEndDate !== appliedCommentEndDate ||
      commentUserFilter !== appliedCommentUserFilter
    );
  };

  // Check if there are applied filters (for Clear button)
  const hasAppliedDonationFilters = () => {
    return appliedDonationTypeFilter || appliedDonationStatusFilter;
  };

  const hasAppliedReminderFilters = () => {
    return appliedReminderTypeFilter || appliedReminderStatusFilter;
  };

  const hasAppliedTransactionFilters = () => {
    return (
      appliedTransactionPaymentMethodFilter ||
      appliedTransactionStartDate ||
      appliedTransactionEndDate
    );
  };

  const hasAppliedCommentFilters = () => {
    return (
      appliedCommentSearch ||
      appliedCommentStartDate ||
      appliedCommentEndDate ||
      appliedCommentUserFilter
    );
  };

  // const hasAppliedEmailCampaignFilters = () => {
  //   return emailCampaignSearch;
  // };

  // const hasAppliedWhatsappCampaignFilters = () => {
  //   return whatsappCampaignSearch;
  // };

  const hasActiveFilters = () => {
    if (activeTab === 1) return hasEmailCampaignFilters();
    if (activeTab === 2) return hasWhatsappCampaignFilters();
    if (activeTab === 3) return hasDonationFilters();
    if (activeTab === 4) return hasReminderFilters();
    if (activeTab === 5) return hasTransactionFilters();
    if (activeTab === 6) return hasCommentFilters();
    return false;
  };

  // Clear filter functions
  // Apply filter handlers
  const handleApplyDonationFilters = () => {
    setAppliedDonationTypeFilter(donationTypeFilter);
    setAppliedDonationStatusFilter(donationStatusFilter);
  };

  const handleApplyReminderFilters = () => {
    setAppliedReminderTypeFilter(reminderTypeFilter);
    setAppliedReminderStatusFilter(reminderStatusFilter);
  };

  const handleApplyTransactionFilters = () => {
    setAppliedTransactionPaymentMethodFilter(transactionPaymentMethodFilter);
    setAppliedTransactionStartDate(transactionStartDate);
    setAppliedTransactionEndDate(transactionEndDate);
  };

  const handleApplyCommentFilters = () => {
    setAppliedCommentSearch(commentSearch);
    setAppliedCommentStartDate(commentStartDate);
    setAppliedCommentEndDate(commentEndDate);
    setAppliedCommentUserFilter(commentUserFilter);
  };

  // Clear filter functions (unused for email/whatsapp - kept for reference)
  // const handleClearEmailCampaignFilters = () => {
  //   setEmailCampaignSearch("");
  //   setEmailCampaignSortBy("last_activity_at");
  //   setEmailCampaignSortOrder("desc");
  // };

  // const handleClearWhatsappCampaignFilters = () => {
  //   setWhatsappCampaignSearch("");
  //   setWhatsappCampaignSortBy("last_activity_at");
  //   setWhatsappCampaignSortOrder("desc");
  // };

  const handleClearDonationFilters = () => {
    setDonationTypeFilter('');
    setAppliedDonationTypeFilter('');
    setDonationStatusFilter('');
    setAppliedDonationStatusFilter('');
    setDonationSortBy('pledge_start_date');
    setDonationSortOrder('desc');
  };

  const handleClearReminderFilters = () => {
    setReminderTypeFilter('');
    setAppliedReminderTypeFilter('');
    setReminderStatusFilter('');
    setAppliedReminderStatusFilter('');
    setReminderSortBy('next_pledge_date');
    setReminderSortOrder('desc');
  };

  const handleClearTransactionFilters = () => {
    setTransactionPaymentMethodFilter('');
    setAppliedTransactionPaymentMethodFilter('');
    setTransactionStartDate('');
    setAppliedTransactionStartDate('');
    setTransactionEndDate('');
    setAppliedTransactionEndDate('');
    setTransactionSortBy('payment_date');
    setTransactionSortOrder('desc');
  };

  const handleClearCommentFilters = () => {
    setCommentSearch('');
    setAppliedCommentSearch('');
    setCommentStartDate('');
    setAppliedCommentStartDate('');
    setCommentEndDate('');
    setAppliedCommentEndDate('');
    setCommentUserFilter('');
    setAppliedCommentUserFilter('');
  };

  // Sorting helper functions (unused for email/whatsapp - kept for reference)
  // const handleEmailCampaignSort = (field) => {
  //   if (emailCampaignSortBy === field) {
  //     setEmailCampaignSortOrder(
  //       emailCampaignSortOrder === "asc" ? "desc" : "asc",
  //     );
  //   } else {
  //     setEmailCampaignSortBy(field);
  //     setEmailCampaignSortOrder("desc");
  //   }
  // };

  // const handleWhatsappCampaignSort = (field) => {
  //   if (whatsappCampaignSortBy === field) {
  //     setWhatsappCampaignSortOrder(
  //       whatsappCampaignSortOrder === "asc" ? "desc" : "asc",
  //     );
  //   } else {
  //     setWhatsappCampaignSortBy(field);
  //     setWhatsappCampaignSortOrder("desc");
  //   }
  // };

  const handleDonationSort = (field) => {
    if (donationSortBy === field) {
      // Toggle sort order if same field
      setDonationSortOrder(donationSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setDonationSortBy(field);
      setDonationSortOrder('desc');
    }
  };

  const handleReminderSort = (field) => {
    if (reminderSortBy === field) {
      // Toggle sort order if same field
      setReminderSortOrder(reminderSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setReminderSortBy(field);
      setReminderSortOrder('desc');
    }
  };

  const handleTransactionSort = (field) => {
    if (transactionSortBy === field) {
      // Toggle sort order if same field
      setTransactionSortOrder(transactionSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setTransactionSortBy(field);
      setTransactionSortOrder('desc');
    }
  };

  // Filtered and sorted data
  const filteredEmailCampaigns = React.useMemo(() => {
    let filtered = [...emailCampaigns.data];

    if (debouncedEmailCampaignSearch) {
      filtered = filtered.filter((c) =>
        c.campaign_name?.toLowerCase().includes(debouncedEmailCampaignSearch.toLowerCase()),
      );
    }

    return filtered.sort((a, b) => {
      const aVal = a[emailCampaignSortBy] || 0;
      const bVal = b[emailCampaignSortBy] || 0;
      if (emailCampaignSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return bVal > aVal ? 1 : -1;
      }
    });
  }, [
    emailCampaigns.data,
    debouncedEmailCampaignSearch,
    emailCampaignSortBy,
    emailCampaignSortOrder,
  ]);

  const filteredWhatsappCampaigns = React.useMemo(() => {
    let filtered = [...whatsappCampaigns.data];

    if (debouncedWhatsappCampaignSearch) {
      filtered = filtered.filter((c) =>
        c.campaign_name?.toLowerCase().includes(debouncedWhatsappCampaignSearch.toLowerCase()),
      );
    }

    return filtered.sort((a, b) => {
      const aVal = a[whatsappCampaignSortBy] || 0;
      const bVal = b[whatsappCampaignSortBy] || 0;
      if (whatsappCampaignSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return bVal > aVal ? 1 : -1;
      }
    });
  }, [
    whatsappCampaigns.data,
    debouncedWhatsappCampaignSearch,
    whatsappCampaignSortBy,
    whatsappCampaignSortOrder,
  ]);

  const filteredDonations = React.useMemo(() => {
    let filtered = [...donations.data];

    if (appliedDonationTypeFilter) {
      filtered = filtered.filter((d) => {
        if (appliedDonationTypeFilter === 'RECURRING') {
          return d.is_recurring === true || d.is_recurring === 1;
        } else if (appliedDonationTypeFilter === 'ONE_TIME') {
          return d.is_recurring === false || d.is_recurring === 0 || !d.is_recurring;
        }
        return true;
      });
    }

    if (appliedDonationStatusFilter) {
      filtered = filtered.filter((d) => {
        // Use the status field from backend
        return d.status === appliedDonationStatusFilter;
      });
    }

    return filtered.sort((a, b) => {
      let aVal = a[donationSortBy];
      let bVal = b[donationSortBy];

      if (donationSortBy === 'pledge_start_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (donationSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return bVal > aVal ? 1 : -1;
      }
    });
  }, [
    donations.data,
    donationSortBy,
    donationSortOrder,
    appliedDonationTypeFilter,
    appliedDonationStatusFilter,
  ]);

  const filteredReminders = React.useMemo(() => {
    let filtered = [...reminders.data];

    if (appliedReminderTypeFilter) {
      filtered = filtered.filter((r) => {
        if (appliedReminderTypeFilter === 'ONE_TIME') {
          return r.frequency === 'one_time';
        } else if (appliedReminderTypeFilter === 'RECURRING') {
          return r.frequency !== 'one_time';
        }
        return true;
      });
    }

    if (appliedReminderStatusFilter) {
      filtered = filtered.filter((r) => r.status === appliedReminderStatusFilter);
    }

    return filtered.sort((a, b) => {
      const aVal = new Date(a[reminderSortBy]).getTime();
      const bVal = new Date(b[reminderSortBy]).getTime();
      if (reminderSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return bVal > aVal ? 1 : -1;
      }
    });
  }, [
    reminders.data,
    reminderSortBy,
    reminderSortOrder,
    appliedReminderTypeFilter,
    appliedReminderStatusFilter,
  ]);

  const filteredTransactions = React.useMemo(() => {
    let filtered = [...transactions.data];

    // Filter by payment method
    if (appliedTransactionPaymentMethodFilter) {
      filtered = filtered.filter((t) => t.payment_method === appliedTransactionPaymentMethodFilter);
    }

    // Filter by date range
    if (appliedTransactionStartDate) {
      const startDate = new Date(appliedTransactionStartDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((t) => new Date(t.payment_date) >= startDate);
    }

    if (appliedTransactionEndDate) {
      const endDate = new Date(appliedTransactionEndDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((t) => new Date(t.payment_date) <= endDate);
    }

    return filtered.sort((a, b) => {
      let aVal = a[transactionSortBy];
      let bVal = b[transactionSortBy];

      if (transactionSortBy === 'payment_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (transactionSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return bVal > aVal ? 1 : -1;
      }
    });
  }, [
    transactions.data,
    transactionSortBy,
    transactionSortOrder,
    appliedTransactionPaymentMethodFilter,
    appliedTransactionStartDate,
    appliedTransactionEndDate,
  ]);

  const filteredComments = React.useMemo(() => {
    let filtered = [...comments.data];

    // Filter by search text
    if (appliedCommentSearch) {
      filtered = filtered.filter((c) =>
        c.comment_text?.toLowerCase().includes(appliedCommentSearch.toLowerCase()),
      );
    }

    // Filter by date range
    if (appliedCommentStartDate) {
      const startDate = new Date(appliedCommentStartDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((c) => new Date(c.created_at) >= startDate);
    }

    if (appliedCommentEndDate) {
      const endDate = new Date(appliedCommentEndDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((c) => new Date(c.created_at) <= endDate);
    }

    // Filter by user
    if (appliedCommentUserFilter) {
      filtered = filtered.filter((c) =>
        c.author_name?.toLowerCase().includes(appliedCommentUserFilter.toLowerCase()),
      );
    }

    return filtered;
  }, [
    comments.data,
    appliedCommentSearch,
    appliedCommentStartDate,
    appliedCommentEndDate,
    appliedCommentUserFilter,
  ]);

  // Get unique users from comments for the filter dropdown
  const commentUsers = React.useMemo(() => {
    const users = [...new Set(comments.data.map((c) => c.author_name))];
    return users.sort();
  }, [comments.data]);

  // Calculate statistics from contact.statistics
  const contactStats = React.useMemo(() => {
    // Use statistics from backend
    if (contact?.statistics) {
      return {
        totalCampaigns: contact.statistics.campaigns || 0,
        totalEmailsSent: contact.statistics.sent || 0,
        totalEmailsOpened: contact.statistics.opened || 0,
        totalEmailsBounced: contact.statistics.bounced || 0,
        totalDonationAmount: contact.statistics.total_donation_amount || 0,
        donationCurrency: contact.statistics.donation_currency || 'INR',
        totalReminders: contact.statistics.reminders || 0,
        totalEmailFailed: contact.statistics.failed || 0,
        totalWhatsAppSent: contact.statistics.whatsapp_sent || 0,
        totalWhatsAppDelivered: contact.statistics.whatsapp_delivered || 0,
        totalWhatsAppRead: contact.statistics.whatsapp_read || 0,
        totalWhatsAppFailed: contact.statistics.whatsapp_failed || 0,
      };
    }

    // Fallback to zero values if statistics not available
    return {
      totalCampaigns: 0,
      totalEmailsSent: 0,
      totalEmailsOpened: 0,
      totalEmailsBounced: 0,
      totalDonationAmount: 0,
      donationCurrency: 'INR',
      totalReminders: 0,
      totalEmailFailed: 0,
      totalWhatsAppSent: 0,
      totalWhatsAppDelivered: 0,
      totalWhatsAppRead: 0,
      totalWhatsAppFailed: 0,
    };
  }, [contact]);

  // Recent top 10 transactions sorted by payment date (most recent first)
  // Only calculate when transactions are loaded
  const recentTop10Transactions = React.useMemo(() => {
    if (transactions.data.length === 0) return [];
    return [...transactions.data]
      .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))
      .slice(0, 10);
  }, [transactions.data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // if (error || (!contact && id !== "new")) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Button onClick={() => navigate("/contacts")} startIcon={<ArrowBack />}>
  //         Back to Contacts
  //       </Button>
  //     </Box>
  //   );
  // }

  const contactName =
    id === 'new'
      ? 'New Contact'
      : contact?.type === 'INDIVIDUAL'
        ? `${contact.first_name} ${contact.last_name || ''}`
        : contact?.organization_name;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        fontSize: '0.875rem',
        backgroundColor: id !== 'new' ? '#fff' : 'transparent',
        pl: id !== 'new' ? 2 : 0,
        '& .MuiTypography-root': {
          fontSize: 'inherit',
        },
        '& .MuiTypography-h4': {
          fontSize: '1.25rem',
        },
        '& .MuiTypography-h5': {
          fontSize: '1.1rem',
        },
        '& .MuiTypography-h6': {
          fontSize: '1rem',
        },
        '& .MuiTypography-body1': {
          fontSize: '0.875rem',
        },
        '& .MuiTypography-body2': {
          fontSize: '0.8125rem',
        },
        '& .MuiTableCell-root': {
          fontSize: '0.8125rem',
          padding: '8px 12px',
        },
        '& .MuiTableCell-head': {
          fontSize: '0.6875rem',
          padding: '8px 12px',
        },
        '& .MuiTab-root': {
          fontSize: '0.875rem',
        },
        '& .MuiButton-root': {
          fontSize: '0.8125rem',
        },
        '& .MuiTextField-root input': {
          fontSize: '0.8125rem',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.8125rem',
        },
        '& .MuiTableContainer-root': {
          borderRadius: '4px !important',
          overflow: 'hidden !important',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          py: 1.5,
          mx: -2,
          px: 2,
          marginRight: 0,

          backgroundColor: id !== 'new' ? '#fff' : isScrolled ? '#fff' : 'transparent',
          // borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate('/contacts')}
            size="small"
            sx={{
              '&:hover': { color: 'rgba(0, 0, 0, 1)' },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.2rem !important',
                  fontWeight: 700,
                  color: '#000000',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  textTransform: 'capitalize',
                }}
              >
                {contactName}
              </Typography>
              {contact && (
                <Chip
                  label={contact.type}
                  size="small"
                  sx={{
                    backgroundColor: contact.type === 'INDIVIDUAL' ? '#E3F2FD' : '#F3E5F5',
                    color: contact.type === 'INDIVIDUAL' ? '#1976D2' : '#7B1FA2',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: '22px',
                    borderRadius: '4px',
                  }}
                />
              )}
            </Box>
            {contact && (
              <Typography variant="body2" color="text.primary" fontWeight={400}>
                {contact.primary_email || 'No email'}
                {contact.phone_1 && ` • ${contact.phone_1}`}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <>
            {id === 'new' ? (
              // Action buttons for new contact
              <>
                {canAddDonation && !fromDonation && !fromTransaction && (
                  <Box
                    component="a"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isSaving) {
                        handleSaveAndAddDonation();
                      }
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      px: 2,
                      py: 0.75,
                      backgroundColor: 'none',
                      color: colors.primary.main,
                      border: `1px solid ${colors.primary.main}`,
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.6 : 1,
                      '&:hover': {
                        textDecoration: 'none',
                        backgroundColor: `${colors.action.hover} !important`,
                      },
                    }}
                  >
                    {isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <FaRupeeSign fontSize="small" />
                    )}
                    Save & Add Pledge
                  </Box>
                )}
                <Box
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isSaving) {
                      handleSave();
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 0.75,
                    backgroundColor: isSaving ? colors.text.disabled : colors.primary.main,
                    color: colors.primary.contrastText,
                    borderRadius: '4px',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.6 : 1,
                    '&:hover': {
                      textDecoration: 'none',
                      backgroundColor: isSaving ? colors.text.disabled : colors.primary.dark,
                    },
                  }}
                >
                  {isSaving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Save fontSize="small" />
                  )}
                  Save Contact
                </Box>
              </>
            ) : (
              currentUser?.role === 'ADMIN' && (
                <Tooltip
                  title={
                    contact?.can_delete === false
                      ? 'Cannot delete: Contact has enrolled in  campaigns or has donations'
                      : 'Delete'
                  }
                  arrow
                  placement="top"
                >
                  <span
                    style={{
                      cursor: contact?.can_delete === false ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <IconButton
                      size="medium"
                      sx={{
                        color: colors.text.primary,
                        p: 1,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: colors.error.main,
                        },
                        '&.Mui-disabled': {
                          color: colors.text.disabled,
                          cursor: 'not-allowed',
                        },
                      }}
                      onClick={handleDeleteClick}
                      disabled={contact?.can_delete === false}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            )}
          </>
        </Box>
      </Box>

      {/* Tags Edit Popover */}
      <Popover
        open={isEditingTags}
        anchorEl={tagAnchorEl}
        onClose={handleTagsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Box sx={{ p: 2, width: 350, borderRadius: '4px' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderRadius="4px"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000000' }}>
                Edit Tags
              </Typography>
              <Tooltip title="Tags will be used to group the contacts" arrow placement="right">
                <InfoOutlined
                  sx={{
                    fontSize: '0.9rem',
                    color: colors.text.secondary,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            </Box>
            <Box display="flex" gap={1} borderRadius="4px">
              <IconButton
                size="small"
                onClick={handleTagsSave}
                sx={{
                  backgroundColor: colors.action.selected,
                  '&:hover': { backgroundColor: colors.action.hover },
                  width: 32,
                  height: 32,
                }}
              >
                <Check fontSize="small" sx={{ color: colors.success.main }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleTagsClose}
                sx={{
                  backgroundColor: 'rgba(245, 101, 101, 0.1)',
                  '&:hover': { backgroundColor: 'rgba(245, 101, 101, 0.2)' },
                  width: 32,
                  height: 32,
                }}
              >
                <Close fontSize="small" sx={{ color: colors.error.main }} />
              </IconButton>
            </Box>
          </Box>
          <TagAutocomplete
            options={availableTags}
            value={tempSelectedTags}
            onChange={(event, newValue) => setTempSelectedTags(newValue)}
            placeholder="Select tags..."
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FAFAFA',
              },
            }}
          />
        </Box>
      </Popover>

      {/* Tags Category Section */}
      {contact && (
        <Box sx={{ mb: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{
              p: '10px',
              borderRadius: '4px',
            }}
          >
            {/* Left side - Tags */}
            <Box display="flex" alignItems="center" gap={1} flex={1}>
              <LocalOffer
                onClick={handleTagsClick}
                sx={{
                  fontSize: '1.25rem',
                  color: colors.primary.main,
                  cursor: `${tagPermission ? 'pointer' : 'default'}`,
                  '&:hover': {
                    color: `${tagPermission ? colors.primary.dark : colors.primary.main}`,
                  },
                }}
              />
              <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
                {contact.tags && contact.tags.length > 0 ? (
                  <>
                    {contact.tags.slice(0, 4).map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        sx={{
                          backgroundColor: tag.color || 'rgba(139, 95, 191, 0.1)',
                          color: tag.color ? '#fff' : '#8B5FBF',
                          fontWeight: 600,
                          height: '24px',
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                        }}
                      />
                    ))}
                    {contact.tags.length > 4 && (
                      <Chip
                        label={`+${contact.tags.length - 4}`}
                        size="small"
                        onClick={handleTagsClick}
                        sx={{
                          color: '#373dfbff',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(55, 61, 251, 0.08)',
                          },
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '0.8125rem',
                    }}
                  >
                    No tags assigned
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right side - Created By */}
            {contact.created_by_name && !isEditMode && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={{
                  flexShrink: 0,
                }}
              >
                <Person
                  sx={{
                    fontSize: '1rem',
                    color: colors.text.secondary,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Created by
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text.primary,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {contact.created_by_name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Statistics */}
      {contact && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {/* Campaign Overview Card */}
            <Card
              sx={{
                p: 1.5,
                borderRadius: '4px',
                boxShadow: 'none',
                backgroundColor: '#F7FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                <CampaignIcon sx={{ fontSize: '1rem' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Campaign Overview
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Total Campaigns
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalCampaigns}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Amount Collected
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    ₹ {(contactStats.totalDonationAmount || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Email Communication Card */}
            <Card
              sx={{
                p: 1.5,
                borderRadius: '4px',
                boxShadow: 'none',
                backgroundColor: '#F7FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                <EmailIcon sx={{ fontSize: '1rem', color: '#3182CE' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Email Overview
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Sent
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalEmailsSent}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Opened
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalEmailsOpened}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Bounced
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalEmailsBounced}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Failed
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalEmailFailed}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* WhatsApp Communication Card */}
            <Card
              sx={{
                p: 1.5,
                borderRadius: '4px',
                boxShadow: 'none',
                backgroundColor: '#F7FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                <WhatsAppIcon sx={{ fontSize: '1rem', color: '#25D366' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  WhatsApp Overview
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Sent
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalWhatsAppSent}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Delivered
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalWhatsAppDelivered}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Read
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalWhatsAppRead}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      display: 'block',
                    }}
                  >
                    Failed
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2D3748',
                      fontSize: '1.25rem',
                    }}
                  >
                    {contactStats.totalWhatsAppFailed}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
      )}

      {/* Tabs Section */}
      {id !== 'new' && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: '42px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  minHeight: '45px',
                  py: 0.75,
                },
              }}
            >
              <Tab
                icon={<Person fontSize="small" />}
                label="Contact Details"
                iconPosition="start"
              />
              <Tab
                icon={<EmailIcon fontSize="small" />}
                label="Email Campaigns"
                iconPosition="start"
              />
              <Tab
                icon={<WhatsAppIcon fontSize="small" />}
                label="WhatsApp Campaigns"
                iconPosition="start"
              />
              <Tab icon={<FiHeart fontSize="large" />} label="Pledges" iconPosition="start" />
              <Tab
                icon={<Notifications fontSize="small" />}
                label="Reminders"
                iconPosition="start"
              />
              <Tab
                icon={<FaRupeeSign fontSize="small" />}
                label="Transactions"
                iconPosition="start"
              />
              <Tab icon={<CommentIcon fontSize="small" />} label="Comments" iconPosition="start" />
            </Tabs>
          </Box>
          {/* Filters Section - Always visible for all tabs */}
          {activeTab !== 0 && (
            <Collapse in={true}>
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  backgroundColor: '#F9FAFB',
                  borderRadius: '4px',
                  mt: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  {/* Email Campaigns Filters */}
                  {activeTab === 1 && (
                    <>
                      <TextField
                        size="small"
                        placeholder="Search email campaigns..."
                        value={emailCampaignSearch}
                        onChange={(e) => setEmailCampaignSearch(e.target.value)}
                        InputProps={{
                          endAdornment: emailCampaignSearch ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setEmailCampaignSearch('')}>
                                <Close fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : (
                            <Search fontSize="small" color="disabled" />
                          ),
                        }}
                        sx={{ width: 250 }}
                      />
                    </>
                  )}

                  {/* WhatsApp Campaigns Filters */}
                  {activeTab === 2 && (
                    <>
                      <TextField
                        size="small"
                        placeholder="Search WhatsApp campaigns..."
                        value={whatsappCampaignSearch}
                        onChange={(e) => setWhatsappCampaignSearch(e.target.value)}
                        InputProps={{
                          endAdornment: whatsappCampaignSearch ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setWhatsappCampaignSearch('')}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : (
                            <Search fontSize="small" color="disabled" />
                          ),
                        }}
                        sx={{ width: 250 }}
                      />
                    </>
                  )}

                  {/* Donations Filters */}
                  {activeTab === 3 && (
                    <>
                      <TextField
                        select
                        size="small"
                        label="Type"
                        value={donationTypeFilter}
                        onChange={(e) => setDonationTypeFilter(e.target.value)}
                        sx={{
                          width: 150,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          All
                        </MenuItem>
                        <MenuItem value="ONE_TIME" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          One Time
                        </MenuItem>
                        <MenuItem value="RECURRING" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Recurring
                        </MenuItem>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Status"
                        value={donationStatusFilter}
                        onChange={(e) => setDonationStatusFilter(e.target.value)}
                        sx={{
                          width: 130,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          All
                        </MenuItem>
                        <MenuItem value="PENDING" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Pending
                        </MenuItem>
                        <MenuItem value="PAUSED" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Paused
                        </MenuItem>
                        <MenuItem value="CLOSED" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Closed
                        </MenuItem>
                      </TextField>
                      {/* Apply and Clear Buttons beside last filter */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={handleApplyDonationFilters}
                          disabled={!hasActiveFilters()}
                          sx={{
                            mr: -1,
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Apply
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleClearDonationFilters}
                          disabled={!hasAppliedDonationFilters()}
                          sx={{
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Clear
                        </IconButton>
                      </Box>
                    </>
                  )}

                  {/* Reminders Filters */}
                  {activeTab === 4 && (
                    <>
                      <TextField
                        select
                        size="small"
                        label="Type"
                        value={reminderTypeFilter}
                        onChange={(e) => setReminderTypeFilter(e.target.value)}
                        sx={{
                          width: 150,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          All
                        </MenuItem>
                        <MenuItem value="ONE_TIME" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          One Time
                        </MenuItem>
                        <MenuItem value="RECURRING" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Recurring
                        </MenuItem>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Status"
                        value={reminderStatusFilter}
                        onChange={(e) => setReminderStatusFilter(e.target.value)}
                        sx={{
                          width: 130,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          All
                        </MenuItem>
                        <MenuItem value="PENDING" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Pending
                        </MenuItem>
                        <MenuItem value="COMPLETED" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Completed
                        </MenuItem>
                      </TextField>
                      {/* Apply and Clear Buttons beside last filter */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={handleApplyReminderFilters}
                          disabled={!hasActiveFilters()}
                          sx={{
                            mr: -1,
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Apply
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleClearReminderFilters}
                          disabled={!hasAppliedReminderFilters()}
                          sx={{
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Clear
                        </IconButton>
                      </Box>
                    </>
                  )}

                  {/* Transactions Filters */}
                  {activeTab === 5 && (
                    <>
                      <TextField
                        select
                        size="small"
                        label="Payment Method"
                        value={transactionPaymentMethodFilter}
                        onChange={(e) => setTransactionPaymentMethodFilter(e.target.value)}
                        sx={{
                          width: 180,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          All
                        </MenuItem>
                        <MenuItem value="CASH" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Cash
                        </MenuItem>
                        <MenuItem value="CHEQUE" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Cheque
                        </MenuItem>
                        <MenuItem value="BANK_TRANSFER" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Bank Transfer
                        </MenuItem>
                        <MenuItem value="UPI" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          UPI
                        </MenuItem>
                        <MenuItem value="CARD" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Card
                        </MenuItem>
                        <MenuItem value="OTHER" sx={{ p: '2px 8px', minHeight: '32px' }}>
                          Other
                        </MenuItem>
                      </TextField>
                      <TextField
                        type="date"
                        size="small"
                        label="Start Date"
                        value={transactionStartDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setTransactionStartDate(newStartDate);
                          // If end date is before new start date, clear it
                          if (transactionEndDate && newStartDate > transactionEndDate) {
                            setTransactionEndDate('');
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          onClick: (e) => e.target.showPicker?.(),
                        }}
                        sx={{
                          width: 160,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                      <TextField
                        type="date"
                        size="small"
                        label="End Date"
                        value={transactionEndDate}
                        onChange={(e) => setTransactionEndDate(e.target.value)}
                        inputProps={{
                          min: transactionStartDate || undefined,
                          onClick: (e) => e.target.showPicker?.(),
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: 160,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                      {/* Apply and Clear Buttons beside last filter */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={handleApplyTransactionFilters}
                          disabled={!hasActiveFilters()}
                          sx={{
                            mr: -1,
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Apply
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleClearTransactionFilters}
                          disabled={!hasAppliedTransactionFilters()}
                          sx={{
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Clear
                        </IconButton>
                      </Box>
                    </>
                  )}

                  {/* Comments Filters */}
                  {activeTab === 6 && (
                    <>
                      <TextField
                        size="small"
                        placeholder="Search comments..."
                        value={commentSearch}
                        onChange={(e) => setCommentSearch(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleApplyCommentFilters();
                          }
                        }}
                        InputProps={{
                          endAdornment: commentSearch ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCommentSearch('');
                                  setAppliedCommentSearch('');
                                }}
                                sx={{ padding: '4px' }}
                              >
                                <FiX size={16} style={{ color: colors.text.secondary }} />
                              </IconButton>
                            </InputAdornment>
                          ) : (
                            <InputAdornment position="end">
                              <Search fontSize="small" sx={{ color: '#737B8B' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: 230,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                        }}
                      />
                      <TextField
                        type="date"
                        size="small"
                        label="Comments From"
                        value={commentStartDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setCommentStartDate(newStartDate);
                          // If end date is before new start date, clear it
                          if (commentEndDate && newStartDate > commentEndDate) {
                            setCommentEndDate('');
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          onClick: (e) => e.target.showPicker?.(),
                        }}
                        sx={{
                          width: 160,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                      <TextField
                        type="date"
                        size="small"
                        label="Comments To"
                        value={commentEndDate}
                        onChange={(e) => setCommentEndDate(e.target.value)}
                        inputProps={{
                          min: commentStartDate || undefined,
                          onClick: (e) => e.target.showPicker?.(),
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: 160,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                      <TextField
                        select
                        size="small"
                        label="User"
                        value={commentUserFilter}
                        onChange={(e) => setCommentUserFilter(e.target.value)}
                        sx={{
                          width: 160,
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            backgroundColor: '#FAFAFA',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value="">All Users</MenuItem>
                        {commentUsers.map((user) => (
                          <MenuItem key={user} value={user}>
                            {user}
                          </MenuItem>
                        ))}
                      </TextField>
                      {/* Apply and Clear Buttons beside last filter */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={handleApplyCommentFilters}
                          disabled={!hasActiveFilters()}
                          sx={{
                            mr: -1,
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Apply
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleClearCommentFilters}
                          disabled={!hasAppliedCommentFilters()}
                          sx={{
                            backgroundColor: 'transparent',
                            color: colors.primary.main,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 510,
                            p: '8px',
                            '&:disabled': {
                              color: colors.text.disabled,
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          Clear
                        </IconButton>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Collapse>
          )}
        </Box>
      )}

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>
        {id === 'new' ? (
          // NEW CONTACT - Show simple form layout
          <Box display="flex" gap={2}>
            {/* Left side - Form Fields (Vertical) */}
            <Card
              sx={{
                width: '65%',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  pb: 2,
                  borderBottom: `1px solid ${colors.border.main}`,
                  color: colors.text.primary,
                }}
              >
                Contact Information
              </Typography>
              <Box>
                <Box mb={2.5}>
                  <TextField
                    select
                    size="medium"
                    fullWidth
                    label="Contact Type"
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                    }}
                  >
                    <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                    <MenuItem value="ORGANIZATION">Organization</MenuItem>
                  </TextField>
                </Box>

                {formData.type === 'INDIVIDUAL' ? (
                  <>
                    <Box mb={1.5}>
                      <TextField
                        fullWidth
                        inputProps={{
                          maxLength: 50,
                        }}
                        label="First Name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 50) {
                            setFormData({ ...formData, first_name: value });
                            if (validationErrors.first_name) {
                              setValidationErrors({
                                ...validationErrors,
                                first_name: undefined,
                              });
                            }
                          }
                        }}
                        required
                        error={!!validationErrors.first_name}
                        helperText={validationErrors.first_name}
                        FormHelperTextProps={{
                          sx: { marginLeft: 0 },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      />
                      {!validationErrors.first_name && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textAlign: 'right',
                            mt: 0.5,
                            color: 'text.secondary',
                          }}
                        >
                          {(formData.first_name || '').length} / 50
                        </Typography>
                      )}
                    </Box>
                    <Box mb={1.5}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 50) {
                            setFormData({ ...formData, last_name: value });
                            if (validationErrors.last_name) {
                              setValidationErrors({
                                ...validationErrors,
                                last_name: undefined,
                              });
                            }
                          }
                        }}
                        error={!!validationErrors.last_name}
                        helperText={validationErrors.last_name}
                        FormHelperTextProps={{
                          sx: { marginLeft: 0 },
                        }}
                        inputProps={{
                          maxLength: 50,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      />
                      {!validationErrors.last_name && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textAlign: 'right',
                            mt: 0.5,
                            color: 'text.secondary',
                          }}
                        >
                          {(formData.last_name || '').length} / 50
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box mb={2.5}>
                    <TextField
                      fullWidth
                      label="Organization Name"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 50) {
                          setFormData({
                            ...formData,
                            organization_name: value,
                          });
                          if (validationErrors.organization_name) {
                            setValidationErrors({
                              ...validationErrors,
                              organization_name: undefined,
                            });
                          }
                        }
                      }}
                      required
                      error={!!validationErrors.organization_name}
                      helperText={validationErrors.organization_name}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      inputProps={{
                        maxLength: 50,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                      }}
                    />
                    {!validationErrors.organization_name && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          mt: 0.5,
                          color: 'text.secondary',
                        }}
                      >
                        {(formData.organization_name || '').length} / 50
                      </Typography>
                    )}
                  </Box>
                )}

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="Primary Email (Email or Phone required)"
                    name="primary_email"
                    type="email"
                    value={formData.primary_email}
                    onChange={handleFormChange}
                    error={!!validationErrors.primary_email}
                    helperText={validationErrors.primary_email}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 }, // Remove left margin on error text
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                </Box>

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="Secondary Email"
                    name="secondary_email"
                    type="email"
                    value={formData.secondary_email}
                    onChange={handleFormChange}
                    error={!!validationErrors.secondary_email}
                    helperText={validationErrors.secondary_email}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                </Box>

                <Box mb={2.5}>
                  <Grid container spacing={1}>
                    <Grid item xs={2.5}>
                      <TextField
                        select
                        fullWidth
                        label={
                          formData.phone_1 && formData.phone_1.trim() !== '' ? 'Code *' : 'Code'
                        }
                        name="phone_1_country_code"
                        value={formData.phone_1_country_code}
                        onChange={handleFormChange}
                        error={!!validationErrors.phone_1_country_code}
                        helperText={validationErrors.phone_1_country_code}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      >
                        <MenuItem value="1">+1 (US/CA)</MenuItem>
                        <MenuItem value="44">+44 (UK)</MenuItem>
                        <MenuItem value="91">+91 (IN)</MenuItem>
                        <MenuItem value="61">+61 (AU)</MenuItem>
                        <MenuItem value="86">+86 (CN)</MenuItem>
                        <MenuItem value="81">+81 (JP)</MenuItem>
                        <MenuItem value="49">+49 (DE)</MenuItem>
                        <MenuItem value="33">+33 (FR)</MenuItem>
                        <MenuItem value="971">+971 (AE)</MenuItem>
                        <MenuItem value="65">+65 (SG)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={9.5}>
                      <TextField
                        fullWidth
                        label="Primary Phone (Email or Phone required)"
                        name="phone_1"
                        type="text"
                        value={formData.phone_1}
                        onChange={handleFormChange}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        error={!!validationErrors.phone_1}
                        helperText={validationErrors.phone_1}
                        FormHelperTextProps={{
                          sx: { marginLeft: 0 },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box mb={2.5}>
                  <Grid container spacing={1}>
                    <Grid item xs={2.5}>
                      <TextField
                        select
                        fullWidth
                        label={
                          formData.phone_2 && formData.phone_2.trim() !== '' ? 'Code *' : 'Code'
                        }
                        name="phone_2_country_code"
                        value={formData.phone_2_country_code}
                        onChange={handleFormChange}
                        error={!!validationErrors.phone_2_country_code}
                        helperText={validationErrors.phone_2_country_code}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      >
                        <MenuItem value="1">+1 (US/CA)</MenuItem>
                        <MenuItem value="44">+44 (UK)</MenuItem>
                        <MenuItem value="91">+91 (IN)</MenuItem>
                        <MenuItem value="61">+61 (AU)</MenuItem>
                        <MenuItem value="86">+86 (CN)</MenuItem>
                        <MenuItem value="81">+81 (JP)</MenuItem>
                        <MenuItem value="49">+49 (DE)</MenuItem>
                        <MenuItem value="33">+33 (FR)</MenuItem>
                        <MenuItem value="971">+971 (AE)</MenuItem>
                        <MenuItem value="65">+65 (SG)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={9.5}>
                      <TextField
                        fullWidth
                        type="text"
                        label="Secondary Phone"
                        name="phone_2"
                        value={formData.phone_2}
                        onChange={handleFormChange}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        error={!!validationErrors.phone_2}
                        helperText={validationErrors.phone_2}
                        FormHelperTextProps={{
                          sx: { marginLeft: 0 },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="PAN Card Number"
                    name="pan_card_number"
                    value={formData.pan_card_number}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      if (value.length <= 10) {
                        handleFormChange({
                          target: { name: 'pan_card_number', value },
                        });
                      }
                    }}
                    placeholder="ABCDE1234F"
                    error={!!validationErrors.pan_card_number}
                    helperText={
                      validationErrors.pan_card_number ||
                      'Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)'
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    inputProps={{
                      maxLength: 10,
                      style: { textTransform: 'uppercase' },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                    }}
                  />
                </Box>

                <Box mb={1.5}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    name="address_line1"
                    value={formData.address_line1}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        setFormData({ ...formData, address_line1: value });
                      }
                    }}
                    inputProps={{
                      maxLength: 100,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    {(formData.address_line1 || '').length} / 100
                  </Typography>
                </Box>

                <Box mb={1.5}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        setFormData({ ...formData, address_line2: value });
                      }
                    }}
                    inputProps={{
                      maxLength: 100,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    {(formData.address_line2 || '').length} / 100
                  </Typography>
                </Box>

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleFormChange}
                    error={!!validationErrors.city}
                    helperText={validationErrors.city}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                </Box>

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleFormChange}
                    error={!!validationErrors.state}
                    helperText={validationErrors.state}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                </Box>

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleFormChange}
                    error={!!validationErrors.postal_code}
                    helperText={validationErrors.postal_code}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                </Box>

                <Box mb={2.5}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleFormChange}
                    error={!!validationErrors.country}
                    helperText={validationErrors.country}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                </Box>

                <Box mb={2.5}>
                  <TextField
                    select
                    fullWidth
                    label="Preferred Contact Method"
                    name="preferred_channel"
                    value={formData.preferred_channel}
                    onChange={handleFormChange}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                    }}
                  >
                    <MenuItem value="EMAIL">Email</MenuItem>
                    <MenuItem value="CALL">Call</MenuItem>
                    <MenuItem value="SMS">SMS</MenuItem>
                  </TextField>
                </Box>

                <Box mb={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: '0.875rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    Opt-out Preferences
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="do_not_email"
                        checked={formData.do_not_email}
                        onChange={handleFormChange}
                      />
                    }
                    label="Do Not Email"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="do_not_call"
                        checked={formData.do_not_call}
                        onChange={handleFormChange}
                      />
                    }
                    label="Do Not Call"
                    sx={{ ml: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="do_not_whatsapp"
                        checked={formData.do_not_whatsapp}
                        onChange={handleFormChange}
                      />
                    }
                    label="Do Not WhatsApp"
                    sx={{ ml: 2 }}
                  />
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        setFormData({ ...formData, notes: value });
                      }
                    }}
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 1000 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    {(formData.notes || '').length} / 1000
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Right side - Tags (Separate Card) */}
            <Card
              sx={{
                width: '35%',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                p: 3,
                alignSelf: 'flex-start',
              }}
            >
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tags
                  </Typography>
                  <Tooltip title="Tags will be used to group the contacts" arrow placement="right">
                    <InfoOutlined
                      sx={{
                        fontSize: '0.85rem',
                        color: colors.text.secondary,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                </Box>
                <TagAutocomplete
                  options={availableTags}
                  value={selectedTags}
                  onChange={(event, newValue) => setSelectedTags(newValue)}
                  placeholder="Select tags"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.8rem',
                    },
                  }}
                />
              </Box>
            </Card>
          </Box>
        ) : contact ? (
          // EXISTING CONTACT - Show 3-column layout with inline edit mode
          <>
            <Grid container spacing={2}>
              {/* Column 1 - Contact Information */}
              <Grid item xs={12} md={4} position={'relative'}>
                <Card
                  sx={{
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    height: '600px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Edit/Save/Cancel Buttons in top right corner */}

                  {canEdit && (
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      gap={0.5}
                      sx={{
                        p: 1,
                        pb: 0,
                        position: 'relative',
                        zIndex: 10,
                      }}
                    >
                      {!isEditMode ? (
                        <Box
                          component="a"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleEdit();
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2,
                            py: 0.5,
                            backgroundColor: colors.primary.main,
                            color: colors.primary.contrastText,
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'none',
                              backgroundColor: colors.primary.dark,
                            },
                          }}
                        >
                          Edit
                        </Box>
                      ) : (
                        <>
                          <Box
                            component="a"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isSaving) {
                                handleSave();
                              }
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 2,
                              py: 0.5,

                              backgroundColor: isSaving
                                ? colors.text.disabled
                                : colors.primary.main,
                              color: colors.primary.contrastText,
                              borderRadius: '4px',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              cursor: isSaving ? 'not-allowed' : 'pointer',
                              opacity: isSaving ? 0.6 : 1,
                              '&:hover': {
                                textDecoration: 'none',
                                backgroundColor: isSaving
                                  ? colors.text.disabled
                                  : colors.primary.dark,
                              },
                            }}
                          >
                            Save
                          </Box>

                          <Box
                            component="a"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isSaving) {
                                handleCancelEdit();
                              }
                            }}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 2,
                              py: 0.5,
                              backgroundColor: isSaving ? colors.text.disabled : colors.error.main,
                              color: colors.primary.contrastText,
                              borderRadius: '4px',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              cursor: isSaving ? 'not-allowed' : 'pointer',
                              opacity: isSaving ? 0.6 : 1,
                              '&:hover': {
                                textDecoration: 'none',
                                backgroundColor: isSaving
                                  ? colors.text.disabled
                                  : colors.error.dark,
                              },
                            }}
                          >
                            Cancel
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      p: 2,
                      pt: canEdit ? 1 : 1,
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'transparent transparent',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(136, 136, 136, 0)',
                        borderRadius: '4px',
                        transition: 'background-color 0.3s ease',
                      },
                      '&:hover': {
                        scrollbarColor: '#888 transparent',
                      },
                      '&:hover::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(136, 136, 136, 0.7)',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#555 !important',
                      },
                    }}
                  >
                    {isEditMode ? (
                      // EDIT MODE - Show form fields inline
                      <>
                        <Box mb={2.5}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Contact Type"
                            name="type"
                            value={formData.type}
                            onChange={handleFormChange}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          >
                            <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                            <MenuItem value="ORGANIZATION">Organization</MenuItem>
                          </TextField>
                        </Box>

                        {formData.type === 'INDIVIDUAL' ? (
                          <>
                            <Box mb={1.5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="First Name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 50) {
                                    setFormData({
                                      ...formData,
                                      first_name: value,
                                    });
                                    if (validationErrors.first_name) {
                                      setValidationErrors({
                                        ...validationErrors,
                                        first_name: undefined,
                                      });
                                    }
                                  }
                                }}
                                required
                                error={!!validationErrors.first_name}
                                helperText={validationErrors.first_name}
                                FormHelperTextProps={{
                                  sx: { marginLeft: 0 },
                                }}
                                inputProps={{ maxLength: 50 }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                  },
                                }}
                              />
                              {!validationErrors.first_name && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textAlign: 'right',
                                    color: 'text.secondary',
                                    fontSize: '0.1rem',
                                  }}
                                >
                                  {(formData.first_name || '').length} / 50
                                </Typography>
                              )}
                            </Box>
                            <Box mb={1.5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Last Name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 50) {
                                    setFormData({
                                      ...formData,
                                      last_name: value,
                                    });
                                    if (validationErrors.last_name) {
                                      setValidationErrors({
                                        ...validationErrors,
                                        last_name: undefined,
                                      });
                                    }
                                  }
                                }}
                                error={!!validationErrors.last_name}
                                helperText={validationErrors.last_name}
                                FormHelperTextProps={{
                                  sx: { marginLeft: 0 },
                                }}
                                inputProps={{ maxLength: 50 }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                  },
                                }}
                              />
                              {!validationErrors.last_name && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textAlign: 'right',
                                    color: 'text.secondary',
                                  }}
                                >
                                  {(formData.last_name || '').length} / 50
                                </Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          <Box mb={2.5}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Organization Name"
                              name="organization_name"
                              value={formData.organization_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 50) {
                                  setFormData({
                                    ...formData,
                                    organization_name: value,
                                  });
                                  if (validationErrors.organization_name) {
                                    setValidationErrors({
                                      ...validationErrors,
                                      organization_name: undefined,
                                    });
                                  }
                                }
                              }}
                              required
                              error={!!validationErrors.organization_name}
                              helperText={validationErrors.organization_name}
                              FormHelperTextProps={{
                                sx: { marginLeft: 0 },
                              }}
                              inputProps={{ maxLength: 50 }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '4px',
                                },
                              }}
                            />
                            {!validationErrors.organization_name && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  textAlign: 'right',
                                  mt: 0.5,
                                  color: 'text.secondary',
                                }}
                              >
                                {(formData.organization_name || '').length} / 50
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Primary Email (Email or Phone required)"
                            name="primary_email"
                            type="email"
                            value={formData.primary_email}
                            onChange={handleFormChange}
                            error={!!validationErrors.primary_email}
                            helperText={validationErrors.primary_email}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Secondary Email"
                            name="secondary_email"
                            type="email"
                            value={formData.secondary_email}
                            onChange={handleFormChange}
                            error={!!validationErrors.secondary_email}
                            helperText={validationErrors.secondary_email}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={2.5}>
                          <Grid container spacing={1}>
                            <Grid item xs={3}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={
                                  formData.phone_1 && formData.phone_1.trim() !== ''
                                    ? 'Code *'
                                    : 'Code'
                                }
                                name="phone_1_country_code"
                                value={formData.phone_1_country_code}
                                onChange={handleFormChange}
                                error={!!validationErrors.phone_1_country_code}
                                helperText={validationErrors.phone_1_country_code}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                  },
                                }}
                              >
                                <MenuItem value="1">+1 (US/CA)</MenuItem>
                                <MenuItem value="44">+44 (UK)</MenuItem>
                                <MenuItem value="91">+91 (IN)</MenuItem>
                                <MenuItem value="61">+61 (AU)</MenuItem>
                                <MenuItem value="86">+86 (CN)</MenuItem>
                                <MenuItem value="81">+81 (JP)</MenuItem>
                                <MenuItem value="49">+49 (DE)</MenuItem>
                                <MenuItem value="33">+33 (FR)</MenuItem>
                                <MenuItem value="971">+971 (AE)</MenuItem>
                                <MenuItem value="65">+65 (SG)</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={9}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Primary Phone (Email or Phone required)"
                                name="phone_1"
                                type="text"
                                value={formData.phone_1}
                                onChange={handleFormChange}
                                onKeyPress={(e) => {
                                  // Prevent non-numeric characters
                                  if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                error={!!validationErrors.phone_1}
                                helperText={validationErrors.phone_1}
                                FormHelperTextProps={{
                                  sx: { marginLeft: 0 },
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>

                        <Box mb={2.5}>
                          <Grid container spacing={1}>
                            <Grid item xs={3}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={
                                  formData.phone_2 && formData.phone_2.trim() !== ''
                                    ? 'Code *'
                                    : 'Code'
                                }
                                name="phone_2_country_code"
                                value={formData.phone_2_country_code}
                                onChange={handleFormChange}
                                error={!!validationErrors.phone_2_country_code}
                                helperText={validationErrors.phone_2_country_code}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                  },
                                }}
                              >
                                <MenuItem value="1">+1 (US/CA)</MenuItem>
                                <MenuItem value="44">+44 (UK)</MenuItem>
                                <MenuItem value="91">+91 (IN)</MenuItem>
                                <MenuItem value="61">+61 (AU)</MenuItem>
                                <MenuItem value="86">+86 (CN)</MenuItem>
                                <MenuItem value="81">+81 (JP)</MenuItem>
                                <MenuItem value="49">+49 (DE)</MenuItem>
                                <MenuItem value="33">+33 (FR)</MenuItem>
                                <MenuItem value="971">+971 (AE)</MenuItem>
                                <MenuItem value="65">+65 (SG)</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={9}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Secondary Phone"
                                name="phone_2"
                                type="text"
                                value={formData.phone_2}
                                onChange={handleFormChange}
                                onKeyPress={(e) => {
                                  // Prevent non-numeric characters
                                  if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                error={!!validationErrors.phone_2}
                                helperText={validationErrors.phone_2}
                                FormHelperTextProps={{
                                  sx: { marginLeft: 0 },
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="PAN Card Number"
                            name="pan_card_number"
                            value={formData.pan_card_number}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              if (value.length <= 10) {
                                handleFormChange({
                                  target: { name: 'pan_card_number', value },
                                });
                              }
                            }}
                            placeholder="ABCDE1234F"
                            error={!!validationErrors.pan_card_number}
                            helperText={
                              validationErrors.pan_card_number ||
                              'Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)'
                            }
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            inputProps={{
                              maxLength: 10,
                              style: { textTransform: 'uppercase' },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={1.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Address Line 1"
                            name="address_line1"
                            value={formData.address_line1}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 100) {
                                setFormData({
                                  ...formData,
                                  address_line1: value,
                                });
                              }
                            }}
                            inputProps={{ maxLength: 100 }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'right',

                              color: 'text.secondary',
                            }}
                          >
                            {(formData.address_line1 || '').length} / 100
                          </Typography>
                        </Box>

                        <Box mb={1.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Address Line 2"
                            name="address_line2"
                            value={formData.address_line2}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 100) {
                                setFormData({
                                  ...formData,
                                  address_line2: value,
                                });
                              }
                            }}
                            inputProps={{ maxLength: 100 }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'right',

                              color: 'text.secondary',
                            }}
                          >
                            {(formData.address_line2 || '').length} / 100
                          </Typography>
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="City"
                            name="city"
                            value={formData.city}
                            onChange={handleFormChange}
                            error={!!validationErrors.city}
                            helperText={validationErrors.city}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="State"
                            name="state"
                            value={formData.state}
                            onChange={handleFormChange}
                            error={!!validationErrors.state}
                            helperText={validationErrors.state}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Postal Code"
                            name="postal_code"
                            value={formData.postal_code}
                            onChange={handleFormChange}
                            error={!!validationErrors.postal_code}
                            helperText={validationErrors.postal_code}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Country"
                            name="country"
                            value={formData.country}
                            onChange={handleFormChange}
                            error={!!validationErrors.country}
                            helperText={validationErrors.country}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>

                        <Box mb={1.5}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Preferred Contact Method"
                            name="preferred_channel"
                            value={formData.preferred_channel}
                            onChange={handleFormChange}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          >
                            <MenuItem value="EMAIL">Email</MenuItem>
                            <MenuItem value="CALL">Call</MenuItem>
                            <MenuItem value="SMS">SMS</MenuItem>
                          </TextField>
                        </Box>

                        <Box mb={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                            }}
                          >
                            Opt-out Preferences
                          </Typography>
                          <Box ml={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="do_not_email"
                                  checked={formData.do_not_email}
                                  onChange={handleFormChange}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Do Not Email</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="do_not_call"
                                  checked={formData.do_not_call}
                                  onChange={handleFormChange}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Do Not Call</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="do_not_whatsapp"
                                  checked={formData.do_not_whatsapp}
                                  onChange={handleFormChange}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Do Not WhatsApp</Typography>}
                            />
                          </Box>
                        </Box>

                        <Box mb={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Notes"
                            name="notes"
                            value={formData.notes}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 1000) {
                                setFormData({ ...formData, notes: value });
                              }
                            }}
                            multiline
                            rows={3}
                            inputProps={{ maxLength: 1000 }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'right',
                              mt: 0.5,
                              color: 'text.secondary',
                            }}
                          >
                            {(formData.notes || '').length} / 1000
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      // VIEW MODE - Show contact information
                      <>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Type
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.type}
                          </Typography>
                        </Box>
                        {contact.type === 'INDIVIDUAL' ? (
                          <>
                            <Box mb={2.5}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 0.5, fontSize: '0.95rem' }}
                              >
                                First Name
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '1rem',
                                  color: colors.text.primary,
                                }}
                              >
                                {contact.first_name}
                              </Typography>
                            </Box>
                            <Box mb={2.5}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ mb: 0.5, fontSize: '0.95rem' }}
                              >
                                Last Name
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '1rem',
                                  color: colors.text.primary,
                                }}
                              >
                                {contact.last_name || '--'}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Box mb={2.5}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mb: 0.5, fontSize: '0.95rem' }}
                            >
                              Organization Name
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#000',
                              }}
                            >
                              {contact.organization_name}
                            </Typography>
                          </Box>
                        )}
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Primary Email
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.primary_email || '--'}
                          </Typography>
                        </Box>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Secondary Email
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.secondary_email || '--'}
                          </Typography>
                        </Box>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Phone 1
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.phone_1_country_code && contact.phone_1
                              ? `+${contact.phone_1_country_code}${contact.phone_1}`
                              : '--'}
                          </Typography>
                        </Box>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Phone 2
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.phone_2_country_code && contact.phone_2
                              ? `+${contact.phone_2_country_code}${contact.phone_2}`
                              : '--'}
                          </Typography>
                        </Box>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            PAN Card Number
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                              letterSpacing: '0.5px',
                            }}
                          >
                            {contact.pan_card_number || '--'}
                          </Typography>
                        </Box>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 0.5, fontSize: '0.95rem' }}
                          >
                            Address
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {[
                              contact.address_line1,
                              contact.address_line2,
                              contact.city,
                              contact.state,
                              contact.postal_code,
                              contact.country,
                            ]
                              .filter(Boolean)
                              .join(', ') || '--'}
                          </Typography>
                        </Box>
                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Preferred Channel
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.preferred_channel || '--'}
                          </Typography>
                        </Box>

                        <Box mb={2.5}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Opt-Out Preferences
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {Boolean(contact.do_not_email) && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    color: colors.text.primary,
                                  }}
                                >
                                  Do Not Email
                                </Typography>
                              </Box>
                            )}
                            {Boolean(contact.do_not_call) && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    color: colors.text.primary,
                                  }}
                                >
                                  Do Not Call
                                </Typography>
                              </Box>
                            )}
                            {Boolean(contact.do_not_whatsapp) && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    color: colors.text.primary,
                                  }}
                                >
                                  Do Not WhatsApp
                                </Typography>
                              </Box>
                            )}
                            {!contact.do_not_email &&
                              !contact.do_not_call &&
                              !contact.do_not_whatsapp && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    color: colors.text.secondary,
                                  }}
                                >
                                  None
                                </Typography>
                              )}
                          </Box>
                        </Box>

                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              mb: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            Notes
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '1rem',
                              color: colors.text.primary,
                            }}
                          >
                            {contact.notes || 'No notes'}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Column 2 - Comments Interface */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    height: '600px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {comments.loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                      <CircularProgress />
                    </Box>
                  ) : comments.error ? (
                    <Box p={3}>
                      <Alert
                        severity="error"
                        action={
                          <IconButton size="small" onClick={loadComments}>
                            <Refresh />
                          </IconButton>
                        }
                      >
                        {comments.error}
                      </Alert>
                    </Box>
                  ) : (
                    <>
                      {/* Comments Feed */}
                      <Box
                        sx={{
                          flex: 1,
                          overflowY: 'auto',
                          px: 2,
                          py: 3,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        {filteredComments.length === 0 ? (
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                          >
                            <CommentIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                              {comments.length === 0
                                ? 'No comments yet'
                                : 'No comments match your filters'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {comments.length === 0
                                ? 'Start the conversation by adding a comment below'
                                : 'Try adjusting your date range or user filter'}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            {filteredComments.map((comment, index) => (
                              <React.Fragment key={comment.id}>
                                <Box
                                  ref={
                                    index === filteredComments.length - 1 ? commentsEndRef : null
                                  }
                                  sx={{
                                    mb: 1,
                                  }}
                                >
                                  {/* Author and timestamp header */}
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                  >
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Box
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          backgroundColor: colors.primary.main,
                                          color: '#ffffff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 700,
                                          fontSize: '0.6rem',
                                        }}
                                      >
                                        {comment.author_name.charAt(0).toUpperCase()}
                                      </Box>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#000000',
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {comment.author_name}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#737B8B',
                                          fontSize: '0.75rem',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {new Date(comment.created_at).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}
                                      </Typography>
                                      {canEdit &&
                                        (currentUser.role === 'ADMIN' ||
                                          comment.author_id === currentUser.id) && (
                                          <Tooltip title="Delete comment">
                                            <IconButton
                                              onClick={() => handleDeleteComment(comment.id)}
                                              sx={{
                                                color: colors.text.primary,
                                                '&:hover': {
                                                  color: colors.error.main,
                                                },
                                              }}
                                            >
                                              <FiTrash2 size={16} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                    </Box>
                                  </Box>

                                  {/* Comment text */}
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: colors.text.primary,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.5,
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                        backgroundColor: '#F7FAFC',
                                        p: 1,
                                        borderRadius: '4px',
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                      }}
                                    >
                                      {comment.comment_text}
                                    </Typography>
                                  </Box>
                                </Box>
                                {index < filteredComments.length - 1 && <Divider sx={{ mb: 2 }} />}
                              </React.Fragment>
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* Comment Input */}
                      {canEdit && (
                        <Box
                          sx={{
                            p: 1,
                            borderTop: '1px solid #e5e7eb',
                            backgroundColor: colors.background.sidebar,
                          }}
                        >
                          <Box display="flex" gap={1} alignItems="flex-start">
                            <Box sx={{ flex: 1, position: 'relative' }}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={3}
                                placeholder="Add a comment..."
                                value={newComment}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 2000) {
                                    setNewComment(value);
                                  }
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (newComment.trim() && !submittingComment) {
                                      handleAddComment();
                                    }
                                  }
                                }}
                                inputProps={{
                                  maxLength: 2000,
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    padding: '8px',
                                    borderRadius: '4px',
                                    backgroundColor: '#ffffff',
                                  },
                                }}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || submittingComment}
                              startIcon={
                                submittingComment ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <Send />
                                )
                              }
                              sx={{
                                backgroundColor: colors.primary.main,
                                color: colors.secondary.contrastText,
                                textTransform: 'none',
                                borderRadius: '4px',
                                fontWeight: 600,
                                px: 2,
                                minWidth: '80px',
                                height: '40px',
                                mt: 0,
                                '&:hover': {
                                  backgroundColor: colors.primary.dark,
                                },
                                '&:disabled': {
                                  backgroundColor: colors.action.row,
                                  color: colors.text.disabled,
                                },
                              }}
                            >
                              {submittingComment ? 'Posting...' : 'Post'}
                            </Button>
                          </Box>
                          {/* <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: "block", fontSize: "0.7rem" }}>
                                Press Enter to post, Shift+Enter for new line
                              </Typography> */}
                        </Box>
                      )}
                    </>
                  )}
                </Card>
              </Grid>

              {/* Column 3 - Quick-View Panels */}
              <Grid item xs={12} md={4}>
                {/* Recent Top 10 Transactions Panel */}
                <Card
                  sx={{
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    mb: 2,
                    height: '600px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Recent Top 10 Transactions
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#888',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: '#555',
                        },
                      },
                    }}
                  >
                    {transactions.loading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress size={32} />
                      </Box>
                    ) : recentTop10Transactions.length === 0 ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                      >
                        <FaRupeeSign sx={{ fontSize: 48, color: '#ddd', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No transactions available
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        {recentTop10Transactions.map((transaction, index) => (
                          <Box
                            key={transaction.id}
                            sx={{
                              p: 2,
                              borderBottom:
                                index < recentTop10Transactions.length - 1
                                  ? '1px solid #E5E7EB'
                                  : 'none',
                              '&:hover': {
                                backgroundColor: '#FAFAFA',
                              },
                            }}
                          >
                            {/* Row 1: Date on Left, Payment Method on Right */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#737B8B',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                }}
                              >
                                {transaction.payment_date
                                  ? new Date(transaction.payment_date).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                    })
                                  : 'Date not available'}
                              </Typography>

                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: '4px',
                                  backgroundColor: '#E0E7FF',
                                  color: '#3730A3',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {transaction.payment_method || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Row 2: Amount */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: '#C05621',
                                  fontSize: '1.25rem',
                                }}
                              >
                                ₹{transaction.amount ? transaction.amount.toLocaleString() : '0'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ p: 1, borderTop: '1px solid #E5E7EB' }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={transactions.loading || recentTop10Transactions.length === 0}
                      onClick={() => setActiveTab(5)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '4px',
                        backgroundColor: colors.primary.main,
                        color: colors.secondary.contrastText,
                        '&:hover': {
                          backgroundColor: colors.primary.dark,
                          borderColor: colors.primary.dark,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: colors.action.row,
                          color: colors.text.disabled,
                          borderColor: colors.action.row,
                          cursor: 'not-allowed',
                        },
                      }}
                    >
                      View all
                    </Button>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : (
          // Fallback - should not reach here
          <Box p={3} textAlign="center">
            <Typography color="text.secondary">Loading contact information...</Typography>
          </Box>
        )}
      </TabPanel>

      {/* Email Campaigns Tab */}
      <TabPanel value={activeTab} index={1}>
        <Card sx={{ borderRadius: '4px', borderShadow: 'none' }}>
          {emailCampaigns.loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : emailCampaigns.error ? (
            <Box p={3}>
              <Alert
                severity="error"
                action={
                  <IconButton size="small" onClick={loadEmailCampaigns}>
                    <Refresh />
                  </IconButton>
                }
              >
                {emailCampaigns.error}
              </Alert>
            </Box>
          ) : filteredEmailCampaigns.length === 0 ? (
            <Box p={4} textAlign="center">
              <CampaignIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                {emailCampaignSearch
                  ? 'No email campaigns match your filters'
                  : 'No email campaign activity found'}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ border: 'none', borderShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#F7FAFC',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Campaign Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Emails Sent
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Emails Opened
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Emails Bounced
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Emails Failed
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Email Unsubscribe
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmailCampaigns.map((campaign, index) => (
                    <TableRow
                      key={campaign.campaign_id}
                      onClick={() => navigateWithPermission(`/campaigns/${campaign.campaign_id}`)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(90, 106, 207, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '14px !important' }}>
                        <Tooltip title={campaign.campaign_name} arrow>
                          <Box
                            sx={{
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {campaign.campaign_name}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.emails_sent > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.success.main,
                              fontSize: '1.5rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.5rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.emails_opened > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.primary.main,
                              fontSize: '1.5rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.5rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.emails_bounced > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.error.main,
                              fontSize: '1.5rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.5rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.emails_failed > 0 ? (
                          <CheckCircle
                            sx={{
                              color: '#DC2626',
                              fontSize: '1.5rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.5rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.email_unsubscribe > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.warning.main,
                              fontSize: '1.5rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.5rem',
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {/* WhatsApp Campaigns Tab */}
      <TabPanel value={activeTab} index={2}>
        <Card sx={{ borderRadius: '4px', borderShadow: 'none' }}>
          {whatsappCampaigns.loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : whatsappCampaigns.error ? (
            <Box p={3}>
              <Alert
                severity="error"
                action={
                  <IconButton size="small" onClick={loadWhatsappCampaigns}>
                    <Refresh />
                  </IconButton>
                }
              >
                {whatsappCampaigns.error}
              </Alert>
            </Box>
          ) : filteredWhatsappCampaigns.length === 0 ? (
            <Box p={4} textAlign="center">
              <CampaignIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                {whatsappCampaignSearch
                  ? 'No WhatsApp campaigns match your filters'
                  : 'No WhatsApp campaign activity found'}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ border: 'none', borderShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#F7FAFC',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Campaign Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Messages Sent
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Messages Delivered
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Messages Read
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        py: '10px',
                        px: '12px',
                        color: colors.text.primary,
                        textAlign: 'center',
                      }}
                    >
                      Messages Failed
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWhatsappCampaigns.map((campaign, index) => (
                    <TableRow
                      key={campaign.campaign_id}
                      onClick={() => navigateWithPermission(`/campaigns/${campaign.campaign_id}`)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(90, 106, 207, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '14px !important' }}>
                        <Tooltip title={campaign.campaign_name} arrow>
                          <Box
                            sx={{
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {campaign.campaign_name}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.messages_sent > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.success.main,
                              fontSize: '1.25rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.25rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.messages_delivered > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.success.main,
                              fontSize: '1.25rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.25rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.messages_read > 0 ? (
                          <CheckCircle
                            sx={{
                              color: colors.primary.main,
                              fontSize: '1.25rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.25rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px !important',
                          textAlign: 'center',
                        }}
                      >
                        {campaign.messages_failed > 0 ? (
                          <CheckCircle
                            sx={{
                              color: '#DC2626',
                              fontSize: '1.25rem',
                            }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{
                              color: colors.text.disabled,
                              fontSize: '1.25rem',
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {/* Donations Tab */}
      <TabPanel value={activeTab} index={3}>
        <Card sx={{ borderRadius: '4px', borderShadow: 'none' }}>
          {donations.loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : donations.error ? (
            <Box p={3}>
              <Alert
                severity="error"
                action={
                  <IconButton size="small" onClick={loadDonations}>
                    <Refresh />
                  </IconButton>
                }
              >
                {donations.error}
              </Alert>
            </Box>
          ) : filteredDonations.length === 0 ? (
            <Box p={4} textAlign="center">
              <FaRupeeSign sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                {donationTypeFilter || donationStatusFilter
                  ? 'No donations match your filters'
                  : 'No donations found'}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ border: 'none', borderShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#F7FAFC',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(90, 106, 207, 0.04)',
                        },
                      }}
                      onClick={() => handleDonationSort('pledge_start_date')}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        Start Date
                        {getSortIcon(donationSortBy, donationSortOrder, 'pledge_start_date')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(90, 106, 207, 0.04)',
                        },
                      }}
                      onClick={() => handleDonationSort('amount')}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        Pledge Amount
                        {getSortIcon(donationSortBy, donationSortOrder, 'amount')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Collected Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Remaing Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Frequency
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Pledge Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDonations.map((donation, index) => (
                    <TableRow
                      key={donation.id}
                      onClick={() => navigateWithPermission(`/donations/${donation.id}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        {donation.pledge_start_date
                          ? new Date(donation.pledge_start_date).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        ₹{donation.amount}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        ₹{donation.collected_amount}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        ₹{donation.amount - donation.collected_amount}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        {getFrequencyLabel(donation.frequency) || '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        {
                          <Chip
                            label={donation.status}
                            size="small"
                            sx={{
                              borderRadius: '4px',
                              fontSize: '11px',
                              height: '22px',

                              backgroundColor:
                                donation.status === 'PENDING'
                                  ? `#FEF3C7`
                                  : donation.status === 'PAUSED'
                                    ? `${colors.warning?.main || '#ff9800'}15`
                                    : donation.status === 'CLOSED'
                                      ? `${colors.text.disabled}15`
                                      : colors.action.hover,
                              color:
                                donation.status === 'PENDING'
                                  ? '#92400E'
                                  : donation.status === 'PAUSED'
                                    ? colors.warning?.main || '#ff9800'
                                    : donation.status === 'CLOSED'
                                      ? colors.text.disabled
                                      : colors.text.secondary,
                            }}
                          />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {/* Transactions Tab */}
      <TabPanel value={activeTab} index={5}>
        <Card sx={{ borderRadius: '4px', borderShadow: 'none', border: 'none' }}>
          {transactions.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : transactions.error ? (
            <Box p={3}>
              <Alert
                severity="error"
                action={
                  <IconButton size="small" onClick={loadTransactions}>
                    <Refresh />
                  </IconButton>
                }
              >
                {transactions.error}
              </Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Transaction ID
                    </TableCell>
                    <TableCell
                      onClick={() => handleTransactionSort('payment_date')}
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        Payment Date
                        {getSortIcon(transactionSortBy, transactionSortOrder, 'payment_date')}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleTransactionSort('amount')}
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        Amount
                        {getSortIcon(transactionSortBy, transactionSortOrder, 'amount')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Payment Method
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        textAlign: 'center',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransactions.map((transaction) => (
                      <TableRow
                        onClick={() => navigateWithPermission(`/transactions/${transaction.id}`)}
                        key={transaction.id}
                        hover
                        sx={{
                          '&:hover': { backgroundColor: '#F9FAFB !important' },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: '13px !important',
                            color: colors.text.primary,
                            py: '12px',
                            px: '12px',
                          }}
                        >
                          #{transaction.id}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '13px !important',
                            color: colors.text.primary,
                            py: '12px',
                            px: '12px',
                          }}
                        >
                          {new Date(transaction.payment_date).toLocaleDateString('en-GB')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '13px !important',
                            color: colors.text.primary,
                            py: '12px',
                            px: '12px',
                          }}
                        >
                          ₹{transaction.amount}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '13px !important',
                            color: colors.text.primary,
                            py: '12px',
                            px: '12px',
                          }}
                        >
                          {paymentMethodLabel(transaction.payment_method) || '--'}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '13px !important',
                            py: '12px',
                            px: '12px',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Tooltip title="Preview Receipt">
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewReceipt(transaction)}
                                sx={{
                                  textAlign: 'center',
                                  color: colors.text.primary,
                                  '&:hover': {
                                    backgroundColor: '#F9FAFB !important',
                                    color: colors.primary.main,
                                  },
                                }}
                              >
                                <GrDocumentDownload fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {/* Comments Tab */}
      <TabPanel value={activeTab} index={6}>
        <Card
          sx={{
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '600px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {comments.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress />
            </Box>
          ) : comments.error ? (
            <Box p={3}>
              <Alert
                severity="error"
                action={
                  <IconButton size="small" onClick={loadComments}>
                    <Refresh />
                  </IconButton>
                }
              >
                {comments.error}
              </Alert>
            </Box>
          ) : (
            <>
              {/* Comments Feed */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 3,
                  backgroundColor: '#ffffff',
                }}
              >
                {filteredComments.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <CommentIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      {comments.length === 0 ? 'No comments yet' : 'No comments match your filters'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {comments.length === 0
                        ? 'Start the conversation by adding a comment below'
                        : 'Try adjusting your date range or user filter'}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {filteredComments.map((comment, index) => (
                      <React.Fragment key={comment.id}>
                        <Box
                          ref={index === filteredComments.length - 1 ? commentsEndRef : null}
                          sx={{
                            mb: 1,
                          }}
                        >
                          {/* Author and timestamp header */}
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  backgroundColor: colors.primary.main,
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 700,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {comment.author_name.charAt(0).toUpperCase()}
                              </Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: '#000000',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {comment.author_name}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#737B8B',
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {new Date(comment.created_at).toLocaleString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Typography>
                              {canEdit &&
                                (currentUser.role === 'ADMIN' ||
                                  comment.author_id === currentUser.id) && (
                                  <Tooltip title="Delete comment">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteComment(comment.id)}
                                      sx={{
                                        color: colors.text.primary,
                                        '&:hover': {
                                          color: colors.error.main,
                                        },
                                      }}
                                    >
                                      <FiTrash2 size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                            </Box>
                          </Box>

                          {/* Comment text */}
                          <Box
                            sx={{
                              p: '10px',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.text.primary,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                lineHeight: 1.5,
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                backgroundColor: '#F7FAFC',
                                p: 1.5,
                                borderRadius: '4px',
                                display: 'inline-block',
                                maxWidth: '100%',
                              }}
                            >
                              {comment.comment_text}
                            </Typography>
                          </Box>
                        </Box>
                        {index < filteredComments.length - 1 && <Divider sx={{ mb: 2 }} />}
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Comment Input */}
              {canEdit && (
                <Box
                  sx={{
                    mb: 0.5,
                    p: 1.5,
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: colors.background.sidebar,
                  }}
                >
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <Box sx={{ flex: 1, position: 'relative' }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 2000) {
                            setNewComment(value);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (newComment.trim() && !submittingComment) {
                              handleAddComment();
                            }
                          }
                        }}
                        inputProps={{
                          maxLength: 2000,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: '8px',
                            borderRadius: '4px',
                            backgroundColor: '#ffffff',
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: '-20px',
                          right: 0,
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                        }}
                      >
                        {newComment.length}/2000
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || submittingComment}
                      startIcon={
                        submittingComment ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Send />
                        )
                      }
                      sx={{
                        backgroundColor: colors.primary.main,
                        color: colors.secondary.contrastText,
                        textTransform: 'none',
                        borderRadius: '4px',
                        fontWeight: 600,
                        px: 2,
                        minWidth: '80px',
                        height: '40px',
                        mt: 0,
                        '&:hover': {
                          backgroundColor: colors.primary.dark,
                        },
                        '&:disabled': {
                          backgroundColor: colors.action.row,
                          color: colors.text.disabled,
                        },
                      }}
                    >
                      {submittingComment ? 'Posting...' : 'Post'}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Card>
      </TabPanel>

      {/* Reminders Tab */}
      <TabPanel value={activeTab} index={4}>
        <Card sx={{ borderRadius: '4px', boxShadow: 'none' }}>
          {reminders.loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : reminders.error ? (
            <Box p={3}>
              <Alert
                severity="error"
                action={
                  <IconButton size="small" onClick={loadReminders}>
                    <Refresh />
                  </IconButton>
                }
              >
                {reminders.error}
              </Alert>
            </Box>
          ) : filteredReminders.length === 0 ? (
            <Box p={4} textAlign="center">
              <Notifications sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                {reminderTypeFilter || reminderStatusFilter
                  ? 'No reminders match your filters'
                  : 'No reminders found'}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ border: 'none', borderShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#F7FAFC',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(90, 106, 207, 0.04)',
                        },
                      }}
                      onClick={() => handleReminderSort('next_pledge_date')}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        Pledge Start Date
                        {getSortIcon(reminderSortBy, reminderSortOrder, 'next_pledge_date')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(90, 106, 207, 0.04)',
                        },
                      }}
                      onClick={() => handleReminderSort('next_pledge_date')}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        Pledge End Date
                        {getSortIcon(reminderSortBy, reminderSortOrder, 'next_pledge_date')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Frequency
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px !important',
                        color: colors.text.primary,
                        py: '10px',
                        px: '12px',
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReminders.map((reminder, index) => (
                    <TableRow
                      key={reminder.id}
                      onClick={() => navigateWithPermission(`/reminders/${reminder.id}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        {reminder.pledge_start_date
                          ? new Date(reminder.pledge_start_date).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        {reminder.pledge_end_date
                          ? new Date(reminder.pledge_end_date).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          color: colors.text.primary,
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        {getFrequencyLabel(reminder.frequency) || '--'}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '13px !important',
                          py: '12px',
                          px: '12px',
                        }}
                      >
                        <Chip
                          label={reminder.status}
                          size="small"
                          sx={{
                            borderRadius: '4px',
                            fontSize: '11px',
                            height: '22px',
                            backgroundColor:
                              reminder.status === 'COMPLETED'
                                ? '#D1FAE5'
                                : reminder.status === 'PENDING'
                                  ? '#FEF3C7'
                                  : '#F3F4F6',
                            color:
                              reminder.status === 'COMPLETED'
                                ? '#065F46'
                                : reminder.status === 'PENDING'
                                  ? '#92400E'
                                  : '#6B7280',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="contact"
      />

      {/* Receipt Preview Modal */}
      <ReceiptPreviewModal
        open={previewOpen}
        onClose={handlePreviewClose}
        content={previewHTML}
        hasEmail={!!previewTransaction?.primary_email}
        hasPhone={!!previewTransaction?.phone_1}
        donorName={previewTransaction?.donor_name || ''}
        onDownload={() => handleDownloadTransactionReceipt(previewTransaction)}
        onSendEmail={() => handleSendTransactionReceiptEmail(previewTransaction)}
        onSendWhatsApp={() => handleSendTransactionReceiptWhatsApp(previewTransaction)}
        emailTemplate={previewTransaction?.email_template_id || ''}
        whatsappTemplate={previewTransaction?.whatsapp_template_id || ''}
      />
    </Box>
  );
}
